import React, { useState, useEffect, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import { vetMedicines } from '../data/vetMedicines';
import { humanMedicines } from '../data/humanMedicines';
import { apiUrl } from '../api';

const generateVchNo = () => 'CA/' + Math.floor(1000 + Math.random() * 9000);

const Sales = () => {
  const navigate = useNavigate();

  const [allPurchases, setAllPurchases] = useState([]);
  const [productCatalog, setProductCatalog] = useState([]);
  const [customerCatalog, setCustomerCatalog] = useState([]);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [showCreditAlertModal, setShowCreditAlertModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [allSales, setAllSales] = useState([]);
  const [allCreditNotes, setAllCreditNotes] = useState([]);
  const [allSalesReturns, setAllSalesReturns] = useState([]);
  const [showPastBillsDrawer, setShowPastBillsDrawer] = useState(false);
  const [editingBillId, setEditingBillId] = useState(null);
  const [searchPastBillQuery, setSearchPastBillQuery] = useState('');
  const [localMedicines, setLocalMedicines] = useState(() => {
    const custom = JSON.parse(localStorage.getItem('customMedicines') || '[]');
    const vetList = vetMedicines.map(m => ({ ...m, category: m.category || 'Vet Medicine' }));
    const humanList = humanMedicines.map(m => ({ ...m, category: m.category || 'Human Medicine' }));
    return [...vetList, ...humanList, ...custom].sort((a, b) => a.name.localeCompare(b.name));
  });
  
  const [showUpiModal, setShowUpiModal] = useState(false);
  const [upiId, setUpiId] = useState(localStorage.getItem('medical_upi_id') || 'yourname@sbi');

  const handleSaveUpiId = (newId) => {
    setUpiId(newId);
    localStorage.setItem('medical_upi_id', newId);
  };
  
  const fileInputRef = useRef(null);
  
  // Header state
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [saleType, setSaleType] = useState('CASH');
  const [billType, setBillType] = useState('Medicine');
  const [vchNo, setVchNo] = useState(1);
  const [totalSalesCount, setTotalSalesCount] = useState(0);
  
  const [custAc, setCustAc] = useState('');
  const [custName, setCustName] = useState('');
  const [custAddr, setCustAddr] = useState('');
  const [custGst, setCustGst] = useState('');
  const [custMob, setCustMob] = useState('');
  
  const [doctorName, setDoctorName] = useState('');
  const [doctorAddr, setDoctorAddr] = useState('');
  const [doctorArea, setDoctorArea] = useState('');
  const [doctorMob, setDoctorMob] = useState('');

  // Customer Modal State
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    address: '',
    gst: '',
    phone: ''
  });

  // Doctor Modal State
  const [doctorCatalog, setDoctorCatalog] = useState([]);
  const [showDoctorSuggestions, setShowDoctorSuggestions] = useState(false);
  const [showDoctorModal, setShowDoctorModal] = useState(false);
  const [newDoctor, setNewDoctor] = useState({
    name: '',
    address: '',
    area: '',
    phone: ''
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (!document.querySelector('.fixed.inset-0')) {
          const saveBtn = document.getElementById('save-bill-btn');
          if (saveBtn && document.activeElement !== saveBtn) {
            e.preventDefault();
            saveBtn.focus();
          } else {
            const hasContent = rows && rows.some(r => r.productName && r.productName.trim() !== '');
            if (hasContent) {
              if (window.confirm("बिलामध्ये औषधे भरलेली आहेत. ड्राफ्ट सुरक्षित सेव्ह राहील. डॅशबोर्डवर जायचे आहे का?")) {
                navigate('/dashboard');
              }
            } else {
              navigate('/dashboard');
            }
          }
        }
      }

      if (e.key === 'Enter') {
        const active = document.activeElement;
        if (!active) return;

        // Skip global handling if modal overlay is open
        if (showCustomerModal || showDoctorModal || showUpiModal || showPastBillsDrawer) {
          return;
        }

        // Customer Details -> Direct to Doctor Details (Doctor Name)
        if (active.id === 'cust-name-input' || active.id === 'cust-addr-input' || active.id === 'cust-mob-input') {
          e.preventDefault();
          document.getElementById('doctor-name-input')?.focus();
          return;
        }

        // Doctor Details -> Direct to Product Table Search
        if (active.id === 'doctor-name-input' || active.id === 'doctor-addr-input' || active.id === 'doctor-area-input' || active.id === 'doctor-mob-input') {
          e.preventDefault();
          const firstProductInput = document.querySelector('tbody tr td input');
          if (firstProductInput) {
            firstProductInput.focus();
          } else {
            document.getElementById('payment-type-select')?.focus();
          }
          return;
        }

        // Payment Type dropdown -> Amount Received input
        if (active.id === 'payment-type-select') {
          e.preventDefault();
          const recInput = document.getElementById('amount-received-input');
          if (recInput) {
            recInput.focus();
            if (recInput.select) recInput.select();
          } else {
            const saveBtn = document.getElementById('save-bill-btn');
            if (saveBtn) saveBtn.focus();
          }
          return;
        }

        // Amount Received input -> Save Bill button
        if (active.id === 'amount-received-input') {
          e.preventDefault();
          const saveBtn = document.getElementById('save-bill-btn');
          if (saveBtn) saveBtn.focus();
          return;
        }

        if (active.tagName === 'BUTTON') {
          return; // Allow button click on Enter
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, showCustomerModal, showDoctorModal, showUpiModal, showPastBillsDrawer]);

  // Row definition based on image columns
  const getInitialRow = (id) => ({
    id,
    productName: '',
    packing: '',
    company: '',
    shelf: '',
    batch: '',
    expDate: '',
    rate: '',
    costPrice: '',
    mrp: '',
    qty: 1,
    disc: '',
    gst: '',
    amount: '',
    showSuggestions: false
  });

  const [rows, setRows] = useState([getInitialRow(Date.now())]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [activeProductSearchRow, setActiveProductSearchRow] = useState(null);
  const [activeProductDetailsRow, setActiveProductDetailsRow] = useState(null);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSuggestionIndex, setProductSuggestionIndex] = useState(0);

  const [showAllRates, setShowAllRates] = useState(false);
  const [visibleRateRowIds, setVisibleRateRowIds] = useState(new Set());
  const [showCostRate, setShowCostRate] = useState(false);
  const [visibleSearchRateIndices, setVisibleSearchRateIndices] = useState(new Set());

  const toggleRowRate = (rowId) => {
    setVisibleRateRowIds(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const toggleAllRates = () => {
    if (showAllRates) {
      setShowAllRates(false);
      setVisibleRateRowIds(new Set());
    } else {
      setShowAllRates(true);
      setVisibleRateRowIds(new Set(rows.map(r => r.id)));
    }
  };

  const toggleSearchRate = (idx) => {
    setVisibleSearchRateIndices(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  // Bottom footer states
  const [narration, setNarration] = useState('');
  const [costRate, setCostRate] = useState('');
  const [roundOff, setRoundOff] = useState('');
  const [itemDisc, setItemDisc] = useState('');
  const [disc, setDisc] = useState('');
  const [discPercent, setDiscPercent] = useState('');
  const [activeDiscountType, setActiveDiscountType] = useState('percent');
  const [schDisc, setSchDisc] = useState('');
  const [cashDisc, setCashDisc] = useState('');
  const [gstPercent, setGstPercent] = useState('');
  const [adjustments, setAdjustments] = useState('');
  const [isAdjPercent, setIsAdjPercent] = useState(false);
  const [mixCashAmount, setMixCashAmount] = useState('');
  const [mixOnlineAmount, setMixOnlineAmount] = useState('');
  const [amountReceived, setAmountReceived] = useState('');

  // Draft Auto-Save & Resume States
  const [hasRestoredDraft, setHasRestoredDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  // 1. Restore draft on initial load
  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem('medical_sale_draft');
      if (savedDraft) {
        const draft = JSON.parse(savedDraft);
        if (draft && draft.rows && draft.rows.length > 0) {
          if (draft.date) setDate(draft.date);
          if (draft.saleType) setSaleType(draft.saleType);
          if (draft.billType) setBillType(draft.billType);
          if (draft.custName !== undefined) setCustName(draft.custName);
          if (draft.custAddr !== undefined) setCustAddr(draft.custAddr);
          if (draft.custGst !== undefined) setCustGst(draft.custGst);
          if (draft.custMob !== undefined) setCustMob(draft.custMob);
          if (draft.doctorName !== undefined) setDoctorName(draft.doctorName);
          if (draft.doctorAddr !== undefined) setDoctorAddr(draft.doctorAddr);
          if (draft.doctorArea !== undefined) setDoctorArea(draft.doctorArea);
          if (draft.doctorMob !== undefined) setDoctorMob(draft.doctorMob);
          if (draft.rows && draft.rows.length > 0) setRows(draft.rows);
          if (draft.discPercent !== undefined) setDiscPercent(draft.discPercent);
          if (draft.activeDiscountType) setActiveDiscountType(draft.activeDiscountType);
          if (draft.disc !== undefined) setDisc(draft.disc);
          if (draft.itemDisc !== undefined) setItemDisc(draft.itemDisc);
          if (draft.schDisc !== undefined) setSchDisc(draft.schDisc);
          if (draft.cashDisc !== undefined) setCashDisc(draft.cashDisc);
          if (draft.gstPercent !== undefined) setGstPercent(draft.gstPercent);
          if (draft.adjustments !== undefined) setAdjustments(draft.adjustments);
          if (draft.isAdjPercent !== undefined) setIsAdjPercent(draft.isAdjPercent);
          if (draft.mixCashAmount !== undefined) setMixCashAmount(draft.mixCashAmount);
          if (draft.mixOnlineAmount !== undefined) setMixOnlineAmount(draft.mixOnlineAmount);
          if (draft.amountReceived !== undefined) setAmountReceived(draft.amountReceived);
          if (draft.roundOff !== undefined) setRoundOff(draft.roundOff);
          if (draft.narration !== undefined) setNarration(draft.narration);
          setDraftSavedAt(draft.savedAt || 'आधीचा ड्राफ्ट');
        }
      }
    } catch (e) {
      console.warn('Could not restore sale draft:', e);
    } finally {
      setHasRestoredDraft(true);
    }
  }, []);

  // 2. Auto-save draft to localStorage as user types
  useEffect(() => {
    if (!hasRestoredDraft || editingBillId) return;

    const hasContent = (rows && rows.some(r => (r.productName && r.productName.trim() !== '') || (parseFloat(r.amount) || 0) > 0)) ||
                       (custName && custName.trim() !== '') ||
                       (doctorName && doctorName.trim() !== '');

    if (hasContent) {
      const draftData = {
        date,
        saleType,
        billType,
        custName, custAddr, custGst, custMob,
        doctorName, doctorAddr, doctorArea, doctorMob,
        rows,
        discPercent, activeDiscountType, disc, itemDisc, schDisc, cashDisc, gstPercent,
        adjustments, isAdjPercent, mixCashAmount, mixOnlineAmount, amountReceived, roundOff, narration,
        savedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        timestamp: Date.now()
      };
      localStorage.setItem('medical_sale_draft', JSON.stringify(draftData));
      setDraftSavedAt(draftData.savedAt);
    } else {
      localStorage.removeItem('medical_sale_draft');
      setDraftSavedAt(null);
    }
  }, [
    hasRestoredDraft, rows, custName, custAddr, custGst, custMob,
    doctorName, doctorAddr, doctorArea, doctorMob,
    saleType, billType, date, discPercent, activeDiscountType, disc, itemDisc,
    schDisc, cashDisc, gstPercent, adjustments, isAdjPercent, mixCashAmount,
    mixOnlineAmount, amountReceived, roundOff, narration, editingBillId
  ]);

  // 3. Clear draft and reset to blank bill
  const handleClearDraft = () => {
    if (window.confirm("चालू ड्राफ्ट बिल क्लिअर करून नवीन रिकामे बिल सुरू करायचे आहे का? (Clear this draft and start a new blank bill?)")) {
      localStorage.removeItem('medical_sale_draft');
      setDraftSavedAt(null);
      setEditingBillId(null);
      setRows([getInitialRow(Date.now())]);
      setDate(new Date().toISOString().split('T')[0]);
      setSaleType('CASH');
      setCustName(''); setCustAddr(''); setCustGst(''); setCustMob('');
      setDoctorName(''); setDoctorAddr(''); setDoctorArea(''); setDoctorMob('');
      setDiscPercent(''); setActiveDiscountType('percent'); setDisc(''); setItemDisc(''); setSchDisc(''); setCashDisc(''); setGstPercent(''); setAdjustments(''); setIsAdjPercent(false); setMixCashAmount(''); setMixOnlineAmount(''); setAmountReceived(''); setRoundOff(''); setNarration('');
    }
  };

  // 4. Warn if navigating or closing tab with unsaved items
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const hasContent = rows && rows.some(r => r.productName && r.productName.trim() !== '');
      if (hasContent && !editingBillId) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [rows, editingBillId]);
  
  // Day of week calculation
  const getDayName = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { weekday: 'long' });
  };

  const cancelEditing = () => {
    setEditingBillId(null);
    setRows([getInitialRow(Date.now())]);
    setDate(new Date().toISOString().split('T')[0]);
    setSaleType('CASH');
    setBillType('Medicine');
    setVchNo(totalSalesCount + 1);
    setCustName('');
    setCustAddr('');
    setCustGst('');
    setCustMob('');
    setDoctorName('');
    setDoctorAddr('');
    setDoctorArea('');
    setDoctorMob('');
    setNarration('');
    setCostRate('');
    setRoundOff('');
    setItemDisc('');
    setDisc('');
    setDiscPercent('');
    setActiveDiscountType('percent');
    setSchDisc('');
    setCashDisc('');
    setGstPercent('');
    setAdjustments('');
    setIsAdjPercent(false);
    setMixCashAmount('');
    setMixOnlineAmount('');
    setAmountReceived('');
  };

  const loadBillForEditing = (bill) => {
    setEditingBillId(bill.id);
    setDate(bill.date || new Date().toISOString().split('T')[0]);
    setSaleType(bill.saleType || 'CASH');
    setBillType(bill.billType || 'Medicine');
    setVchNo(bill.vchNo || '');
    setCustName(bill.custName || '');
    setCustAddr(bill.custAddr || '');
    setCustGst(bill.custGst || '');
    setCustMob(bill.custMob || '');
    setDoctorName(bill.doctorName || '');
    setDoctorAddr(bill.doctorAddr || '');
    setDoctorArea(bill.doctorArea || '');
    setDoctorMob(bill.doctorMob || '');
    setNarration(bill.narration || '');
    setCostRate(bill.costRate || '');
    setRoundOff(bill.roundOff || 0.00);
    setItemDisc(bill.itemDisc || 0.00);
    setDisc(bill.discount || 0.00);
    setDiscPercent(bill.discPercent || 0.00);
    setActiveDiscountType((bill.discount || 0) > 0 ? 'amount' : 'percent');
    setSchDisc(bill.schDisc || 0.00);
    setCashDisc(bill.cashDisc || 0.00);
    setGstPercent(bill.gstPercent || 0);
    setAdjustments(bill.adjustments || 0.00);
    setIsAdjPercent(bill.isAdjPercent || false);
    setMixCashAmount(bill.mixCashAmount || '');
    setMixOnlineAmount(bill.mixOnlineAmount || '');
    setAmountReceived(bill.amountReceived || '');
    
    if (bill.items && bill.items.length > 0) {
      setRows(bill.items.map((item, index) => ({
        ...getInitialRow(Date.now() + index),
        ...item
      })));
    } else {
      setRows([getInitialRow(Date.now())]);
    }
    setShowPastBillsDrawer(false);
  };

  useEffect(() => {
    const fetchPurchases = async () => {
      try {
        const response = await fetch(apiUrl('/api/purchases'));
        if (response.ok) {
          const purchases = await response.json();
          setAllPurchases(purchases);
          let catalog = [];
          purchases.forEach(p => {
            if (p.items) {
              p.items.forEach(item => {
                if (item.productName) {
                  const itemRate = parseFloat(item.rate) || 0;
                  let itemGst = parseFloat(item.gst);
                  if (isNaN(itemGst) || itemGst < 0) itemGst = 0;
                  const fallbackCost = itemRate > 0 ? Math.round(itemRate * (1 + itemGst / 100) * 100) / 100 : itemRate;
                  let itemCost = parseFloat(item.costRate || item.costPrice) || 0;
                  if (itemCost <= 0 || itemCost === itemRate) {
                    itemCost = fallbackCost;
                  }

                  catalog.push({
                    name: item.productName,
                    batch: item.batch || '',
                    exp: item.exp || '',
                    rate: itemRate,
                    costPrice: itemCost,
                    costRate: itemCost,
                    gst: itemGst,
                    mrp: item.mrp || 0,
                    company: item.company || '',
                    packing: item.packing || ''
                  });
                }
              });
            }
          });
          const reversedCatalog = [...catalog].reverse();
          const uniqueCatalog = Array.from(new Set(catalog.map(a => a.name)))
            .map(name => reversedCatalog.find(a => a.name === name));
          setProductCatalog(uniqueCatalog);

          const suppliers = Array.from(new Set(purchases.map(p => p.supplierName).filter(Boolean)))
            .map(name => ({ name, address: '', gst: '', phone: '', isSupplier: true }));
          setCustomerCatalog(prev => {
            const combined = [...prev, ...suppliers];
            return Array.from(new Set(combined.map(c => c.name))).map(name => combined.find(c => c.name === name));
          });
        }
      } catch (error) {
        console.error('Failed to fetch purchases for catalog:', error);
      }
    };
    
    const fetchCustomers = async () => {
      try {
        const response = await fetch(apiUrl('/api/sales'));
        if (response.ok) {
          const sales = await response.json();
          setAllSales(sales);
          setTotalSalesCount(sales.length);
          setVchNo(sales.length + 1);
          let customers = [];
          let doctors = [];
          sales.forEach(s => {
            if (s.custName && s.custName.trim() !== '') {
              customers.push({
                name: s.custName,
                address: s.custAddr || '',
                gst: s.custGst || '',
                phone: s.custMob || '',
                doctorName: s.doctorName || '',
                doctorAddr: s.doctorAddr || '',
                doctorArea: s.doctorArea || '',
                doctorMob: s.doctorMob || ''
              });
            }
            if (s.doctorName && s.doctorName.trim() !== '') {
              doctors.push({
                name: s.doctorName,
                address: s.doctorAddr || '',
                area: s.doctorArea || '',
                phone: s.doctorMob || ''
              });
            }
          });
          // Deduplicate by customer name
          const uniqueCustomers = Array.from(new Set(customers.map(c => c.name)))
            .map(name => customers.find(c => c.name === name));
          setCustomerCatalog(prev => {
            const combined = [...prev, ...uniqueCustomers];
            return Array.from(new Set(combined.map(c => c.name))).map(name => combined.find(c => c.name === name));
          });
          
          // Deduplicate by doctor name
          const uniqueDoctors = Array.from(new Set(doctors.map(d => d.name)))
            .map(name => doctors.find(d => d.name === name));
          setDoctorCatalog(uniqueDoctors);
        }
      } catch (error) {
        console.error('Failed to fetch sales for customer catalog:', error);
      }
    };

    const fetchCreditNotes = async () => {
      try {
        const response = await fetch(apiUrl('/api/credit-notes'));
        if (response.ok) {
          const notes = await response.json();
          setAllCreditNotes(notes);
        }
      } catch (error) {
        console.error('Failed to fetch credit notes for stock:', error);
      }
    };

    const fetchSalesReturns = async () => {
      try {
        const response = await fetch(apiUrl('/api/sales-return'));
        if (response.ok) {
          const returns = await response.json();
          setAllSalesReturns(returns);
        }
      } catch (error) {
        console.error('Failed to fetch sales returns for stock:', error);
      }
    };

    fetchPurchases();
    fetchCustomers();
    fetchCreditNotes();
    fetchSalesReturns();
  }, []);

  const getProductAvailableStock = (productName) => {
    if (!productName) return 0;
    const normalize = (str) => (str || '').trim().replace(/\s+/g, ' ').toUpperCase();
    const nameUpper = normalize(productName);

    const isMatch = (itemStr) => {
      const target = normalize(itemStr);
      if (!target || !nameUpper) return false;
      if (target === nameUpper) return true;

      const getBrand = (s) => s.replace(/[^A-Z0-9]/g, ' ').trim().split(/\s+/)[0];
      const brandTarget = getBrand(target);
      const brandName = getBrand(nameUpper);

      if (brandTarget.length >= 3 && brandTarget === brandName) {
        const tClean = target.replace(/[^A-Z0-9]/g, '');
        const nClean = nameUpper.replace(/[^A-Z0-9]/g, '');
        if (tClean.includes(nClean) || nClean.includes(tClean)) return true;
        if (brandTarget === brandName) return true;
      }
      return false;
    };

    let totalPurchased = 0;
    let totalSold = 0;
    let totalSalesReturned = 0;
    let totalReturnedToSupplier = 0;

    if (allPurchases) {
      allPurchases.forEach(bill => {
        (bill.items || []).forEach(item => {
          if (isMatch(item.productName)) {
            const billedQty = parseFloat(item.qty) || 0;
            let freeVal = parseFloat(item.freeQty || item.free || item.scheme || 0) || 0;
            if (freeVal === 0 && item.sch) {
              const schStr = String(item.sch).trim();
              if (schStr.includes('+')) {
                const parts = schStr.split('+');
                const lastNum = parseFloat(parts[parts.length - 1]);
                if (!isNaN(lastNum)) freeVal = lastNum;
              } else {
                const num = parseFloat(schStr);
                if (!isNaN(num)) freeVal = num;
              }
            }
            totalPurchased += (billedQty + freeVal);
          }
        });
      });
    }

    if (allSales) {
      allSales.forEach(bill => {
        (bill.items || []).forEach(item => {
          if (isMatch(item.productName)) {
            totalSold += (parseFloat(item.qty) || 0);
          }
        });
      });
    }

    if (allSalesReturns) {
      allSalesReturns.forEach(ret => {
        (ret.items || []).forEach(item => {
          if (isMatch(item.productName)) {
            totalSalesReturned += (parseFloat(item.qty) || 0);
          }
        });
      });
    }

    if (allCreditNotes) {
      allCreditNotes.forEach(cn => {
        (cn.items || []).forEach(item => {
          if (isMatch(item.productName)) {
            totalReturnedToSupplier += (parseFloat(item.qty) || 0);
          }
        });
      });
    }

    let currentBillQty = 0;
    if (rows) {
      rows.forEach(row => {
        if (isMatch(row.productName)) {
          currentBillQty += (parseFloat(row.qty) || 0);
        }
      });
    }

    return (totalPurchased + totalSalesReturned) - (totalSold + totalReturnedToSupplier) - currentBillQty;
  };

  const getProductSupplierInfo = (productName, batch) => {
    if (!productName || !allPurchases || allPurchases.length === 0) return 'UNKNOWN / N/A';
    const normalize = (str) => (str || '').trim().replace(/\s+/g, ' ').toUpperCase();
    const nameUpper = normalize(productName);
    const batchUpper = batch ? normalize(batch) : '';

    if (batchUpper) {
      for (const bill of allPurchases) {
        if (bill.items) {
          for (const item of bill.items) {
            if (item.batch && normalize(item.batch) === batchUpper && bill.supplierName) {
              return bill.supplierName;
            }
          }
        }
      }
    }

    const suppliers = [];
    allPurchases.forEach(bill => {
      if (bill.items) {
        bill.items.forEach(item => {
          if (item.productName && (normalize(item.productName) === nameUpper || nameUpper.includes(normalize(item.productName)) || normalize(item.productName).includes(nameUpper))) {
            if (bill.supplierName && !suppliers.includes(bill.supplierName)) {
              suppliers.push(bill.supplierName);
            }
          }
        });
      }
    });

    return suppliers.length > 0 ? suppliers.join(', ') : 'UNKNOWN / N/A';
  };

  const selectProduct = (m, rowId) => {
    const itemRate = parseFloat(m.rate) || 0;
    let itemGst = 0;
    const fallbackCost = itemRate;
    let originalCostPrice = parseFloat(m.costPrice || m.costRate) || 0;
    if (originalCostPrice <= 0 || originalCostPrice === itemRate) {
      originalCostPrice = fallbackCost;
    }
    const mrp = parseFloat(m.mrp) || 0;
    const qty = 1;

    setRows(prevRows => {
      const updatedRows = prevRows.map(r => {
        if (r.id === rowId) {
          return {
            ...r,
            productName: m.name || '',
            company: m.company || '',
            mrp: mrp,
            rate: m.rate || 0,
            costPrice: originalCostPrice,
            qty: qty,
            batch: m.batch || r.batch || '',
            packing: m.packing || m.strip || r.packing || '',
            expDate: m.exp || r.expDate || '',
            gst: 0,
            amount: qty * mrp
          };
        }
        return r;
      });

      // If there is no empty row at the bottom, auto-add a fresh empty row so user can immediately add the next product!
      const hasEmpty = updatedRows.some(r => !r.productName || r.productName.trim() === '');
      if (!hasEmpty) {
        updatedRows.push(getInitialRow(Date.now() + 10));
      }

      return updatedRows;
    });

    setActiveProductSearchRow(null);
    setProductSearchQuery('');
    setSelectedRowId(rowId);
    
    // Open product details popup like in Purchases
    setActiveProductDetailsRow(rowId);
  };

  const removeRow = (id) => {
    if (rows.length > 1) {
      setRows(rows.filter(r => r.id !== id));
    } else {
      setRows([getInitialRow(Date.now())]);
    }
  };

  const addNewRow = () => {
    setRows(prevRows => {
      const existingEmpty = prevRows.find(r => !r.productName || r.productName.trim() === '');
      if (existingEmpty) {
        setTimeout(() => {
          setActiveProductSearchRow(existingEmpty.id);
          setProductSearchQuery('');
          setProductSuggestionIndex(0);
        }, 50);
        return prevRows;
      }
      const newRow = getInitialRow(Date.now());
      setTimeout(() => {
        setActiveProductSearchRow(newRow.id);
        setProductSearchQuery('');
        setProductSuggestionIndex(0);
      }, 50);
      return [...prevRows, newRow];
    });
  };

  const insertRowAfter = (afterId) => {
    setRows(prevRows => {
      const newRow = getInitialRow(Date.now());
      const idx = prevRows.findIndex(r => r.id === afterId);
      let updated;
      if (idx !== -1) {
        updated = [...prevRows.slice(0, idx + 1), newRow, ...prevRows.slice(idx + 1)];
      } else {
        updated = [...prevRows, newRow];
      }
      setTimeout(() => {
        setActiveProductSearchRow(newRow.id);
        setProductSearchQuery('');
        setProductSuggestionIndex(0);
      }, 50);
      return updated;
    });
  };

  const updateRow = (id, field, value) => {
    setRows(prevRows => {
      const updatedRows = prevRows.map((r, index) => {
        if (r.id === id) {
          const updated = { ...r, [field]: value };
          if (['qty', 'rate', 'mrp', 'gst', 'disc'].includes(field)) {
            const qty = parseFloat(updated.qty) || 0;
            const mrp = parseFloat(updated.mrp) || 0;
            updated.amount = qty * mrp;
          }
          return updated;
        }
        return r;
      });

      // If user filled in a product, ensure there is an empty row below ready for additions
      if (field === 'productName' && value && value.toString().trim() !== '') {
        const hasEmpty = updatedRows.some(r => !r.productName || r.productName.trim() === '');
        if (!hasEmpty) {
          updatedRows.push(getInitialRow(Date.now() + 10));
        }
      }

      return updatedRows;
    });
  };

  const handleGridKeyDown = (e) => {
    if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'BUTTON') return;
    const td = e.target.closest('td');
    const tr = e.target.closest('tr');
    if (!td || !tr) {
      if (e.key === 'Enter') {
        e.preventDefault();
        const paySelect = document.getElementById('payment-type-select');
        if (paySelect) paySelect.focus();
      }
      return;
    }

    const tbody = tr.closest('tbody');
    const allTrs = Array.from(tbody.querySelectorAll('tr'));
    const allTds = Array.from(tr.querySelectorAll('td'));
    const rowIndex = allTrs.indexOf(tr);
    const colIndex = allTds.indexOf(td);

    let nextInput = null;
    if (e.key === 'ArrowRight' || e.key === 'Enter') {
      if (e.key === 'ArrowRight' && typeof e.target.selectionStart === 'number' && e.target.selectionStart !== e.target.value?.length && e.target.type !== 'number') return;
      if (e.key === 'Enter') e.preventDefault();
      
      const currentProdInput = tr.querySelector('td:first-child input');
      if (e.key === 'Enter' && ((e.target === currentProdInput && !e.target.value.trim()) || colIndex >= 8)) {
        // If current row has a product, advance to next row or create a new row for next medicine
        if (currentProdInput && currentProdInput.value.trim()) {
          if (rowIndex + 1 < allTrs.length) {
            const nextTr = allTrs[rowIndex + 1];
            const nextProd = nextTr.querySelector('td:first-child input');
            if (nextProd) {
              nextProd.focus();
              return;
            }
          } else {
            addNewRow();
            return;
          }
        }

        const paySelect = document.getElementById('payment-type-select');
        if (paySelect) {
          paySelect.focus();
        }
        return;
      }

      for (let i = colIndex + 1; i < allTds.length; i++) {
        const input = allTds[i].querySelector('input, button');
        if (input && !input.disabled) { nextInput = input; break; }
      }
      if (!nextInput && rowIndex + 1 < allTrs.length) {
        const nextTr = allTrs[rowIndex + 1];
        const nextTds = nextTr.querySelectorAll('td');
        for (let i = 0; i < nextTds.length; i++) {
          const input = nextTds[i].querySelector('input, button');
          if (input && !input.disabled) { nextInput = input; break; }
        }
      } else if (!nextInput && rowIndex === allTrs.length - 1 && e.key === 'Enter') {
        if (currentProdInput && currentProdInput.value.trim()) {
          addNewRow();
        } else {
          const paySelect = document.getElementById('payment-type-select');
          if (paySelect) {
            paySelect.focus();
          }
        }
      }
    } else if (e.key === 'ArrowLeft') {
      if (typeof e.target.selectionEnd === 'number' && e.target.selectionEnd !== 0 && e.target.type !== 'number') return;
      
      for (let i = colIndex - 1; i >= 0; i--) {
        const input = allTds[i].querySelector('input, button');
        if (input && !input.disabled) { nextInput = input; break; }
      }
      if (!nextInput && rowIndex - 1 >= 0) {
        const prevTr = allTrs[rowIndex - 1];
        const prevTds = prevTr.querySelectorAll('td');
        for (let i = prevTds.length - 1; i >= 0; i--) {
          const input = prevTds[i].querySelector('input, button');
          if (input && !input.disabled) { nextInput = input; break; }
        }
      }
    } else if (e.key === 'ArrowUp') {
      if (rowIndex - 1 >= 0) {
        e.preventDefault();
        const prevTr = allTrs[rowIndex - 1];
        nextInput = prevTr.querySelectorAll('td')[colIndex]?.querySelector('input, button');
      }
    } else if (e.key === 'ArrowDown') {
      if (rowIndex + 1 < allTrs.length) {
        e.preventDefault();
        const nextTr = allTrs[rowIndex + 1];
        nextInput = nextTr.querySelectorAll('td')[colIndex]?.querySelector('input, button');
      }
    }

    if (nextInput) {
      nextInput.focus();
      if (nextInput.select) nextInput.select();
    }
  };

  const combinedProducts = Array.from(new Set([...productCatalog, ...localMedicines].map(m => m.name)))
    .map(name => {
      let p = [...productCatalog, ...localMedicines].find(m => m.name === name);
      if (!p.packing && p.name) {
        const match = p.name.match(/\b(\d+(?:\.\d+)?(?:mg|gm|ml|g|mcg|L|kg))\b/i);
        if (match) {
          p = { ...p, packing: match[1] };
        }
      }
      return p;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const validRows = rows.filter(r => r.productName.trim() !== '' || r.amount > 0);
  const totalQty = validRows.reduce((sum, r) => sum + (parseFloat(r.qty) || 0), 0);
  const grossAmount = validRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  
  const discountVal = 0;
  const discPercentVal = 0;
  const percentDiscountAmount = 0;
  const totalDiscount = 0;
  
  const itemDiscVal = 0;
  const schDiscVal = 0;
  const cashDiscVal = 0;
  const adjVal = isAdjPercent ? (grossAmount * (parseFloat(adjustments) || 0)) / 100 : (parseFloat(adjustments) || 0);
  const roundOffVal = parseFloat(roundOff) || 0;
  const gstPercentVal = 0;
  const gstAmountVal = 0;
  
  const netAmount = grossAmount + adjVal + roundOffVal;
  
  const totalCost = validRows.reduce((sum, r) => sum + (parseFloat(r.qty) || 0) * (parseFloat(r.costPrice !== undefined && r.costPrice !== '' ? r.costPrice : r.rate) || 0), 0);
  
  const activeSelectedRow = rows.find(r => r.id === selectedRowId) || (rows.find(r => r.productName) || rows[0]);
  const selectedProductCost = activeSelectedRow && activeSelectedRow.productName 
    ? (parseFloat(activeSelectedRow.costPrice !== undefined && activeSelectedRow.costPrice !== '' ? activeSelectedRow.costPrice : activeSelectedRow.rate) || 0) 
    : 0;

  useEffect(() => {
    if (activeSelectedRow && activeSelectedRow.productName && selectedProductCost > 0) {
      setCostRate(selectedProductCost.toFixed(2));
    } else {
      setCostRate(totalCost.toFixed(2));
    }
  }, [selectedProductCost, totalCost, activeSelectedRow]);

  const handlePrintReceipt = () => {
    if (validRows.length === 0) {
      alert("No items in bill to print!");
      return;
    }
    const printWindow = window.open('', '_blank', 'width=900,height=800');
    if (!printWindow) {
      alert("Please allow popups to print receipt.");
      return;
    }

    const html = `
      <html>
        <head>
          <title>Tax Invoice</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-size: 14px; padding: 40px; color: #333; margin: 0; }
            .header { text-align: center; margin-bottom: 20px; }
            .title { font-size: 24px; font-weight: bold; color: #1e5c5c; }
            .subtitle { font-size: 14px; color: #666; margin-top: 5px; }
            .invoice-label { font-size: 18px; font-weight: bold; text-align: center; margin: 20px 0; letter-spacing: 2px; text-transform: uppercase; border-bottom: 2px solid #eee; padding-bottom: 10px; }
            .info-section { display: flex; justify-content: space-between; margin-bottom: 30px; }
            .info-box { width: 48%; }
            .info-row { display: flex; margin-bottom: 5px; }
            .info-label { font-weight: bold; width: 100px; color: #555; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 30px; border: 1px solid #ddd; }
            th, td { text-align: left; padding: 12px 10px; border-bottom: 1px solid #eee; }
            th { background-color: #f8fafc; font-weight: bold; color: #333; border-bottom: 2px solid #ddd; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .summary-container { display: flex; justify-content: flex-end; }
            .summary-table { width: 350px; border: none; }
            .summary-table td { border: none; padding: 8px 10px; }
            .summary-table .total-row td { border-top: 2px solid #333; font-size: 18px; font-weight: bold; color: #1e5c5c; }
            .footer { margin-top: 50px; text-align: center; font-size: 12px; color: #888; border-top: 1px solid #eee; padding-top: 20px; }
            @media print {
              body { padding: 20px; }
              @page { margin: 0.5cm; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">MEDICAL STORE</div>
            <div class="subtitle">Shop No. 1, Main Market, City | Phone: 9876543210</div>
          </div>
          
          <div class="invoice-label">TAX INVOICE</div>
          
          <div class="info-section">
            <div class="info-box">
              <div class="info-row"><div class="info-label">Customer:</div><div>\${custName || 'Cash'}</div></div>
              \${doctorName ? \`<div class="info-row"><div class="info-label">Doctor:</div><div>\${doctorName}</div></div>\` : ''}
            </div>
            <div class="info-box">
              <div class="info-row"><div class="info-label">Date:</div><div>\${new Date().toLocaleString()}</div></div>
              <div class="info-row"><div class="info-label">Bill No:</div><div>\${Date.now().toString().slice(-6)}</div></div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th width="5%">#</th>
                <th width="35%">Product Description</th>
                <th width="10%" class="text-center">Qty</th>
                <th width="15%" class="text-right">MRP</th>
                <th width="15%" class="text-right">Rate</th>
                <th width="20%" class="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              \${validRows.map((r, i) => \`
                <tr>
                  <td>\${i + 1}</td>
                  <td>
                    <strong>\${r.productName}</strong>
                    \${r.company ? \`<div style="font-size:11px;color:#777;margin-top:2px;">\${r.company}</div>\` : ''}
                  </td>
                  <td class="text-center">\${r.qty}</td>
                  <td class="text-right">\${parseFloat(r.mrp || 0).toFixed(2)}</td>
                  <td class="text-right">\${parseFloat(r.rate || 0).toFixed(2)}</td>
                  <td class="text-right">\${(parseFloat(r.amount) || 0).toFixed(2)}</td>
                </tr>
              \`).join('')}
            </tbody>
          </table>
          
          <div class="summary-container">
            <table class="summary-table">
              <tr>
                <td>Gross Amount:</td>
                <td class="text-right">\${grossAmount.toFixed(2)}</td>
              </tr>
              \${totalDiscount > 0 ? \`
              <tr>
                <td>Discount:</td>
                <td class="text-right">-\${totalDiscount.toFixed(2)}</td>
              </tr>\` : ''}
              \${gstAmountVal > 0 ? \`
              <tr>
                <td>GST Amount:</td>
                <td class="text-right">\${gstAmountVal.toFixed(2)}</td>
              </tr>\` : ''}
              <tr class="total-row">
                <td>Net Payable:</td>
                <td class="text-right">₹ \${Math.round(netAmount).toFixed(2)}</td>
              </tr>
            </table>
          </div>
          
          <div class="footer">
            <p>Thank you for your visit! Get well soon.</p>
            <p>Goods once sold cannot be taken back or exchanged.</p>
          </div>
          
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `;
    
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  useEffect(() => {
    const handleF2 = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        handlePrintReceipt();
      }
    };
    window.addEventListener('keydown', handleF2);
    return () => window.removeEventListener('keydown', handleF2);
  });


  const loadSupplierPurchase = (name) => {
    // If user has already entered items in this bill, do not overwrite them
    if (validRows.length > 0) return;
    // Find all purchases for this supplier/customer
    const supplierPurchases = allPurchases.filter(p => p.supplierName?.toLowerCase() === name.toLowerCase());
    if (supplierPurchases.length > 0) {
      let allItems = [];
      supplierPurchases.forEach(p => {
        if (p.items) allItems.push(...p.items);
      });
      
      // Keep all items separately without merging rows with the same product name
      const uniqueItems = allItems.filter(item => item.productName && item.productName.trim() !== '' && getProductAvailableStock(item.productName) > 0);
      
      if (uniqueItems.length > 0) {
        const newRows = uniqueItems.map((item, idx) => {
          const mrpVal = parseFloat(item.mrp) || parseFloat(item.rate) || 0;
          const itemRate = parseFloat(item.rate) || 0;
          let itemGst = parseFloat(item.gst);
          if (isNaN(itemGst) || itemGst < 0) itemGst = 0;
          const fallbackCost = itemRate > 0 ? Math.round(itemRate * (1 + itemGst / 100) * 100) / 100 : itemRate;
          let costPriceVal = parseFloat(item.costRate || item.costPrice) || 0;
          if (costPriceVal <= 0 || costPriceVal === itemRate) {
            costPriceVal = fallbackCost;
          }

          return {
            id: Date.now() + idx,
            productName: item.productName || '',
            packing: item.packing || item.sch || '',
            company: item.company || '',
            shelf: item.shelf || '',
            batch: item.batch || '',
            expDate: item.exp || '',
            rate: itemRate, // Sync rate with purchase rate
            costPrice: costPriceVal,
            mrp: mrpVal,
            qty: parseFloat(item.qty) || 1,
            gst: itemGst,
            amount: 0, // initially 0, will be recalculated or let user enter qty
            showSuggestions: false
          };
        });
        // Ensure there is one empty row at the end for easy additions
        newRows.push(getInitialRow(Date.now() + newRows.length));
        setRows(newRows);
      }
    }
  };

  const handleSaveBill = async (forcedPaymentType) => {
    if (validRows.length === 0) {
      alert("Please add at least one product with an amount before saving.");
      return;
    }

    try {
      setIsSaving(true);
      const finalSaleType = typeof forcedPaymentType === 'string' ? forcedPaymentType : saleType;
      
      if (finalSaleType === 'CREDIT' && !custName.trim()) {
        setShowCreditAlertModal(true);
        setIsSaving(false);
        return;
      }
      
      const salesData = {
        date,
        day: getDayName(date),
        saleType: finalSaleType,
        billType,
        vchNo,
        custAc, custName, custAddr, custGst, custMob,
        doctorName, doctorAddr, doctorArea, doctorMob,
        narration,
        grossAmount,
        itemDisc: itemDiscVal,
        discount: discountVal,
        discPercent: discPercentVal,
        schDisc: schDiscVal,
        cashDisc: cashDiscVal,
        gstPercent: gstPercentVal,
        gstAmount: gstAmountVal,
        adjustments: parseFloat(adjustments) || 0,
        isAdjPercent: isAdjPercent,
        mixCashAmount: finalSaleType === 'MIX' ? (parseFloat(mixCashAmount) || 0) : 0,
        mixOnlineAmount: finalSaleType === 'MIX' ? (parseFloat(mixOnlineAmount) || 0) : 0,
        amountReceived: parseFloat(amountReceived) || 0,
        amountPaid: (() => {
          const finalNet = Math.round(netAmount);
          let paidVal = parseFloat(amountReceived) || 0;
          if (paidVal === 0) {
            if (finalSaleType === 'CASH' || finalSaleType === 'ONLINE 1' || finalSaleType === 'ONLINE 2' || finalSaleType === 'ONLINE') {
              paidVal = finalNet;
            } else if (finalSaleType === 'MIX') {
              paidVal = (parseFloat(mixCashAmount) || 0) + (parseFloat(mixOnlineAmount) || 0);
            }
          }
          return Math.min(paidVal, finalNet);
        })(),
        balanceDue: (() => {
          const finalNet = Math.round(netAmount);
          let paidVal = parseFloat(amountReceived) || 0;
          if (paidVal === 0) {
            if (finalSaleType === 'CASH' || finalSaleType === 'ONLINE 1' || finalSaleType === 'ONLINE 2' || finalSaleType === 'ONLINE') {
              paidVal = finalNet;
            } else if (finalSaleType === 'MIX') {
              paidVal = (parseFloat(mixCashAmount) || 0) + (parseFloat(mixOnlineAmount) || 0);
            }
          }
          const paidAmount = Math.min(paidVal, finalNet);
          return Math.max(0, finalNet - paidAmount);
        })(),
        returnChange: (parseFloat(amountReceived) || 0) > Math.round(netAmount) ? ((parseFloat(amountReceived) || 0) - Math.round(netAmount)) : 0,
        roundOff: roundOffVal,
        netAmount,
        items: validRows.map(r => ({
          productName: r.productName,
          packing: r.packing,
          company: r.company,
          shelf: r.shelf,
          batch: r.batch,
          expDate: r.expDate,
          rate: parseFloat(r.rate) || 0,
          costPrice: parseFloat(r.costPrice !== undefined && r.costPrice !== '' ? r.costPrice : (parseFloat(r.rate) * (1 + (parseFloat(r.gst || 0) / 100)))) || 0,
          mrp: parseFloat(r.mrp) || 0,
          qty: parseFloat(r.qty) || 0,
          gst: parseFloat(r.gst) || 0,
          amount: parseFloat(r.amount) || 0
        }))
      };

      const method = editingBillId ? 'PUT' : 'POST';
      let url = apiUrl('/api/sales');
      if (editingBillId) {
        url = apiUrl(`/api/sales/${editingBillId}`);
      }

      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(salesData)
      });

      if (!response.ok) throw new Error('Failed to save on backend');
      
      const savedData = await response.json();
      
      if (editingBillId) {
        setAllSales(allSales.map(s => s.id === editingBillId ? savedData : s));
      } else {
        setAllSales(prev => [savedData, ...prev]);
      }
      
      // Save new medicines to local storage so they appear in suggestions next time
      const custom = JSON.parse(localStorage.getItem('customMedicines') || '[]');
      let updatedCustom = false;
      validRows.forEach(r => {
        if (r.productName && r.productName.trim() !== '') {
          const exists = [...vetMedicines, ...custom, ...productCatalog].find(m => m.name?.toLowerCase() === r.productName.trim().toLowerCase());
          if (!exists) {
            custom.push({
              name: r.productName.trim(),
              company: r.company || '',
              mrp: parseFloat(r.mrp) || 0,
              rate: parseFloat(r.rate) || 0
            });
            updatedCustom = true;
          }
        }
      });
      
      if (updatedCustom) {
        localStorage.setItem('customMedicines', JSON.stringify(custom));
        const vetList = vetMedicines.map(m => ({ ...m, category: m.category || 'Vet Medicine' }));
        const humanList = humanMedicines.map(m => ({ ...m, category: m.category || 'Human Medicine' }));
        setLocalMedicines([...vetList, ...humanList, ...custom].sort((a, b) => a.name.localeCompare(b.name)));
      }
      
      alert(editingBillId ? 'Sales Bill Updated Successfully!' : 'Sales Bill Saved Successfully to Firestore!');
      
      // Save customer into customer catalog for future auto-completion
      if (custName.trim()) {
        const savedCust = {
          name: custName.trim(),
          address: custAddr || '',
          gst: custGst || '',
          phone: custMob || '',
          doctorName: doctorName || '',
          doctorAddr: doctorAddr || '',
          doctorArea: doctorArea || '',
          doctorMob: doctorMob || ''
        };
        setCustomerCatalog(prev => {
          const exists = prev.some(c => c.name.toLowerCase() === savedCust.name.toLowerCase());
          if (!exists) return [savedCust, ...prev];
          return prev;
        });
      }

      // Clear draft on successful save
      localStorage.removeItem('medical_sale_draft');
      setDraftSavedAt(null);

      // Reset form
      if (!editingBillId) {
        const newTotal = totalSalesCount + 1;
        setTotalSalesCount(newTotal);
        setVchNo(newTotal + 1);
      }
      
      setEditingBillId(null);
      setRows([getInitialRow(Date.now())]);
      setCustName(''); setCustAddr(''); setCustGst(''); setCustMob('');
      setDoctorName(''); setDoctorAddr(''); setDoctorArea(''); setDoctorMob('');
      setDiscPercent(''); setActiveDiscountType('percent'); setDisc(''); setItemDisc(''); setSchDisc(''); setCashDisc(''); setGstPercent(''); setAdjustments(''); setIsAdjPercent(false); setMixCashAmount(''); setMixOnlineAmount(''); setAmountReceived(''); setRoundOff(''); setNarration('');
    } catch (error) {
      console.error("Error saving bill: ", error);
      alert('Failed to save bill. Make sure your backend server is running.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCustomer = () => {
    if (!newCustomer.name.trim()) {
      alert("Customer Name is required.");
      return;
    }
    const saved = {
      name: newCustomer.name.trim(),
      address: newCustomer.address || '',
      gst: newCustomer.gst || '',
      phone: newCustomer.phone || '',
      doctorName: '',
      doctorAddr: '',
      doctorArea: '',
      doctorMob: ''
    };
    // Update the main form fields with the new customer info
    setCustName(saved.name);
    setCustAddr(saved.address);
    setCustGst(saved.gst);
    setCustMob(saved.phone);
    
    // Add to customerCatalog immediately so it is available in the saved list
    setCustomerCatalog(prev => {
      const exists = prev.some(c => c.name.toLowerCase() === saved.name.toLowerCase());
      if (!exists) return [saved, ...prev];
      return prev;
    });

    // Close modal and reset modal state
    setShowCustomerModal(false);
    setNewCustomer({ name: '', address: '', gst: '', phone: '' });
  };

  const handleSaveDoctor = () => {
    if (!newDoctor.name.trim()) {
      alert("Doctor Name is required.");
      return;
    }
    setDoctorName(newDoctor.name);
    setDoctorAddr(newDoctor.address);
    setDoctorArea(newDoctor.area);
    setDoctorMob(newDoctor.phone);
    
    setShowDoctorModal(false);
    setNewDoctor({ name: '', address: '', area: '', phone: '' });
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayOnline = () => {
    if (validRows.length === 0) {
      alert("Please add at least one product with an amount before paying.");
      return;
    }
    
    setShowUpiModal(true);
  };

  const handleFileChange = (e) => {
    // File change logic if needed
  };

  return (
    <div className="animate-in fade-in duration-700 flex-1 flex flex-col bg-slate-50/50 p-0.5 sm:p-1 font-sans overflow-hidden">
      
      {/* Premium Page Title */}
      <div className="flex items-center justify-between mb-0.5 -mt-1 px-1 shrink-0 w-full">
        <div className="flex items-center gap-2">
          {draftSavedAt && !editingBillId && (
            <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-300 text-emerald-800 px-2.5 py-0.5 rounded-lg text-xs font-bold shadow-xs animate-in fade-in duration-300">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span>💾 ड्राफ्ट सेव्ह आहे ({draftSavedAt})</span>
              <button 
                type="button"
                onClick={handleClearDraft}
                className="ml-1.5 px-2 py-0.2 bg-rose-50 hover:bg-rose-600 text-rose-700 hover:text-white border border-rose-300 rounded text-[10px] font-black transition-colors"
                title="चालू ड्राफ्ट काढून नवीन कोरे बिल सुरू करा"
              >
                नवे बिल (Clear)
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="w-1 h-5 rounded-full bg-gradient-to-b from-teal-400 to-teal-600 shadow-sm shadow-teal-200"></div>
          <h2 className="text-lg sm:text-xl font-black text-slate-800 tracking-tight text-center">
            {editingBillId ? 'Edit Sales Invoice' : 'Sales Entry'}
          </h2>
          {editingBillId && (
            <button onClick={cancelEditing} className="ml-3 px-2.5 py-0.5 bg-rose-100 text-rose-600 rounded text-xs font-bold hover:bg-rose-200 transition-colors">
              Cancel Editing
            </button>
          )}
        </div>
        <div className="flex justify-end">
          <button 
            onClick={() => setShowPastBillsDrawer(true)}
            className="flex items-center gap-1.5 px-3 py-1 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 hover:border-slate-400 font-bold text-xs shadow-sm transition-all"
          >
            <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            View / Edit Past Bills
          </button>
        </div>
      </div>

      {/* Hero Header Section */}
      <div className="flex flex-row gap-1 mb-1 shrink-0">
        
        {/* Left Panel: Customer & Doctor (Glassmorphism look) */}
        <div className="flex-1 bg-white rounded-xl shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07)] border border-slate-100 flex overflow-hidden group">
          
          {/* Customer Side */}
          <div className={`flex-1 p-1 border-r border-slate-100 flex flex-col relative transition-colors duration-500 ${
            saleType === 'CREDIT' ? 'bg-gradient-to-br from-amber-50/40 via-white to-amber-50/20' : 'bg-gradient-to-br from-white to-teal-50/30 hover:to-teal-50/80'
          }`}>
            <div className="flex items-center justify-between mb-1 pb-1 border-b border-slate-100 gap-1 flex-wrap">
              <div className="flex items-center gap-1.5">
                <div className={`p-1 rounded-md transition-colors ${saleType === 'CREDIT' ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-600'}`}>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                </div>
                <span className="text-xs uppercase tracking-widest font-black text-slate-700">Customer Details</span>
                {saleType === 'CREDIT' ? (
                  <span className="text-[10px] font-black text-amber-800 bg-amber-100/90 px-2 py-0.2 rounded-full border border-amber-300">
                    * उधारी (नाव आवश्यक)
                  </span>
                ) : (
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.2 rounded-full border border-emerald-200">
                    रोख (Cash)
                  </span>
                )}
              </div>

              {/* Cash & Credit Option Switcher */}
              <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 shadow-inner gap-0.5">
                <button
                  type="button"
                  onClick={() => setSaleType('CASH')}
                  className={`px-3 py-1 rounded-md text-xs font-black transition-all flex items-center gap-1 ${
                    saleType === 'CASH'
                      ? 'bg-emerald-600 text-white shadow-sm ring-1 ring-emerald-500'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/70'
                  }`}
                  title="Cash Sale (रोख विक्री)"
                >
                  <span>💵 Cash</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSaleType('CREDIT');
                    setShowCustomerSuggestions(true);
                    setTimeout(() => {
                      const input = document.getElementById('cust-name-input');
                      if (input) {
                        input.focus();
                        if (input.select) input.select();
                      }
                    }, 60);
                  }}
                  className={`px-3 py-1 rounded-md text-xs font-black transition-all flex items-center gap-1 ${
                    saleType === 'CREDIT'
                      ? 'bg-amber-600 text-white shadow-sm ring-2 ring-amber-400'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/70'
                  }`}
                  title="Credit Sale (उधारी विक्री - Customer Name Required)"
                >
                  <span>📋 Credit</span>
                </button>
              </div>
            </div>
            
            <div className="relative mb-2">
              <div className="flex items-center justify-between">
                <div className="relative flex-1 flex items-center">
                  <input 
                    id="cust-name-input"
                    type="text" 
                    value={custName}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCustName(val);
                      setShowCustomerSuggestions(true);
                      loadSupplierPurchase(val);
                    }}
                    onFocus={() => setShowCustomerSuggestions(true)}
                    onClick={() => setShowCustomerSuggestions(true)}
                    onBlur={() => {
                      setTimeout(() => setShowCustomerSuggestions(false), 250);
                      const exactMatch = customerCatalog.find(c => c.name.toLowerCase() === custName.toLowerCase());
                      if (exactMatch) {
                        setCustAddr(exactMatch.address);
                        setCustGst(exactMatch.gst);
                        setCustMob(exactMatch.phone);
                        if (exactMatch.doctorName) setDoctorName(exactMatch.doctorName);
                        if (exactMatch.doctorAddr) setDoctorAddr(exactMatch.doctorAddr);
                        if (exactMatch.doctorArea) setDoctorArea(exactMatch.doctorArea);
                        if (exactMatch.doctorMob) setDoctorMob(exactMatch.doctorMob);
                      }
                      loadSupplierPurchase(custName);
                    }}
                    placeholder={saleType === 'CREDIT' ? "Select Saved Customer or Type Name * (Required)" : "Customer Name (Optional for Cash)"}
                    className={`w-full text-sm font-black outline-none border-b uppercase pb-0.5 bg-transparent transition-colors placeholder:font-bold placeholder:text-slate-400 placeholder:normal-case ${
                      saleType === 'CREDIT' 
                        ? (!custName.trim() ? 'border-amber-400 text-amber-900 focus:border-amber-600 placeholder:text-amber-500' : 'border-teal-400 text-teal-900') 
                        : 'border-slate-200 focus:border-teal-400 text-teal-900'
                    }`}
                  />
                  {/* Dropdown toggle chevron */}
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowCustomerSuggestions(prev => !prev)}
                    className="p-1 text-slate-400 hover:text-teal-600 transition-colors"
                    title="View Saved Customers (जतन केलेले ग्राहक)"
                  >
                    <svg className={`w-3.5 h-3.5 transition-transform ${showCustomerSuggestions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                  </button>
                </div>
                <button type="button" tabIndex={-1} onClick={() => setShowCustomerModal(true)} className="ml-1 shrink-0 bg-white hover:bg-teal-50 text-teal-600 p-1 rounded-lg border border-slate-200 shadow-sm transition-all hover:shadow-md hover:border-teal-200 hover:-translate-y-0.5 group-hover:opacity-100" title="Add New Customer">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
                </button>
              </div>

              {/* Customer Suggestions Dropdown - shows all saved customers or filtered list */}
              {showCustomerSuggestions && (
                <div className="absolute z-[100] left-0 top-full mt-1.5 w-[500px] bg-white border-2 border-teal-300 rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.18)] max-h-[50vh] overflow-y-auto">
                  <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase tracking-wider text-slate-500 flex justify-between items-center">
                    <span>Saved Customers ({customerCatalog.length})</span>
                    {saleType === 'CREDIT' && (
                      <span className="text-amber-700 font-bold normal-case">उधारीसाठी ग्राहक निवडा</span>
                    )}
                  </div>
                  {customerCatalog.filter(c => !custName.trim() || c.name.toLowerCase().includes(custName.toLowerCase())).length > 0 ? (
                    customerCatalog
                      .filter(c => !custName.trim() || c.name.toLowerCase().includes(custName.toLowerCase()))
                      .map((c, idx) => (
                        <div 
                          key={idx}
                          className="px-4 py-2 hover:bg-teal-50 cursor-pointer border-b border-slate-100 last:border-0 transition-colors"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setCustName(c.name);
                            setCustAddr(c.address);
                            setCustGst(c.gst);
                            setCustMob(c.phone);
                            if (c.doctorName) setDoctorName(c.doctorName);
                            if (c.doctorAddr) setDoctorAddr(c.doctorAddr);
                            if (c.doctorArea) setDoctorArea(c.doctorArea);
                            if (c.doctorMob) setDoctorMob(c.doctorMob);
                            setShowCustomerSuggestions(false);
                            loadSupplierPurchase(c.name);
                          }}
                        >
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-black text-slate-800">{c.name}</span>
                            {c.phone && <span className="text-[11px] font-bold text-slate-500">📞 {c.phone}</span>}
                          </div>
                          {(c.address || c.gst) && (
                            <div className="text-[10px] font-bold text-slate-400 mt-0.5 flex gap-3">
                              {c.address && <span>📍 {c.address}</span>}
                              {c.gst && <span>📋 GST: {c.gst}</span>}
                            </div>
                          )}
                        </div>
                      ))
                  ) : (
                    <div className="px-4 py-3 text-xs font-bold text-slate-400 text-center bg-slate-50 rounded-xl">
                      {custName.trim() ? `New customer "${custName}" will be saved.` : 'No saved customers found. Click + to add.'}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="mt-auto pt-1 grid grid-cols-2 gap-2">
               <div className="flex flex-col">
                 <span className="text-xs uppercase tracking-wider text-teal-700 font-bold mb-0.5">Address</span>
                 <input id="cust-addr-input" type="text" value={custAddr} onChange={e=>setCustAddr(e.target.value)} className="w-full bg-slate-50/50 rounded flex-1 border border-slate-100 focus:border-teal-300 focus:bg-white outline-none px-1 py-0.5 text-xs font-bold text-slate-700 transition-colors" placeholder="-" />
               </div>

               <div className="flex flex-col">
                 <span className="text-xs uppercase tracking-wider text-teal-700 font-bold mb-0.5">Mobile</span>
                 <input id="cust-mob-input" type="text" value={custMob} onChange={e=>setCustMob(e.target.value)} className="w-full bg-slate-50/50 rounded flex-1 border border-slate-100 focus:border-teal-300 focus:bg-white outline-none px-1 py-0.5 text-xs font-black text-slate-800 tracking-wider transition-colors" placeholder="-" />
               </div>
            </div>
          </div>

          {/* Doctor Side */}
          <div className="flex-1 p-1 flex flex-col relative bg-gradient-to-br from-white to-blue-50/30 hover:to-blue-50/80 transition-colors duration-500">
            <div className="flex items-center gap-1 mb-1">
              <div className="p-1 bg-blue-100 rounded-md text-blue-600"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg></div>
              <span className="text-xs uppercase tracking-widest text-blue-700 font-bold">Doctor Details</span>
            </div>
            
            <div className="relative mb-2">
              <div className="flex items-center justify-between">
                <input 
                  id="doctor-name-input"
                  type="text" 
                  value={doctorName}
                  onChange={(e) => {
                    setDoctorName(e.target.value);
                    setShowDoctorSuggestions(true);
                  }}
                  onFocus={() => setShowDoctorSuggestions(true)}
                  onBlur={() => {
                    setTimeout(() => setShowDoctorSuggestions(false), 200);
                    const exactMatch = doctorCatalog.find(d => d.name.toLowerCase() === doctorName.toLowerCase());
                    if (exactMatch) {
                      setDoctorAddr(exactMatch.address);
                      setDoctorArea(exactMatch.area);
                      setDoctorMob(exactMatch.phone);
                    }
                  }}
                  placeholder="Doctor Name"
                  className="w-full text-sm font-black text-blue-900 outline-none border-b border-slate-100 focus:border-blue-400 uppercase pb-0.5 bg-transparent transition-colors placeholder:font-black placeholder:text-slate-300 placeholder:normal-case"
                />
                <button type="button" tabIndex={-1} onClick={() => setShowDoctorModal(true)} className="ml-2 shrink-0 bg-white hover:bg-blue-50 text-blue-600 p-1 rounded-lg border border-slate-200 shadow-sm transition-all hover:shadow-md hover:border-blue-200 hover:-translate-y-0.5 group-hover:opacity-100" title="Add New Doctor">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
                </button>
              </div>

              {/* Doctor Suggestions Dropdown */}
              {showDoctorSuggestions && doctorName.length > 0 && (
                <div className="absolute z-[100] left-0 top-full mt-2 w-[500px] bg-white border-2 border-blue-200 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.15)] max-h-[50vh] overflow-y-auto">
                  {doctorCatalog.filter(d => d.name.toLowerCase().includes(doctorName.toLowerCase())).length > 0 ? (
                    doctorCatalog
                      .filter(d => d.name.toLowerCase().includes(doctorName.toLowerCase()))
                      .map((d, idx) => (
                        <div 
                          key={idx}
                          className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0 transition-colors"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setDoctorName(d.name);
                            setDoctorAddr(d.address);
                            setDoctorArea(d.area);
                            setDoctorMob(d.phone);
                            setShowDoctorSuggestions(false);
                          }}
                        >
                          <div className="text-xs font-bold text-slate-800">{d.name}</div>
                          <div className="text-xs font-bold text-slate-400 mt-1 flex gap-4">
                            {d.phone && <span>📞 {d.phone}</span>}
                            {d.area && <span>📍 {d.area}</span>}
                          </div>
                        </div>
                      ))
                  ) : (
                    <div className="px-4 py-3 text-xs font-bold text-slate-400 text-center bg-slate-50 rounded-xl">Custom doctor will be saved.</div>
                  )}
                </div>
              )}
            </div>
            
            <div className="mt-auto pt-1 grid grid-cols-3 gap-2">
               <div className="flex flex-col">
                 <span className="text-xs uppercase tracking-wider text-blue-700 font-bold mb-0.5">Address</span>
                 <input id="doctor-addr-input" type="text" value={doctorAddr} onChange={e=>setDoctorAddr(e.target.value)} className="w-full bg-slate-50/50 rounded flex-1 border border-slate-100 focus:border-blue-300 focus:bg-white outline-none px-1 py-0.5 text-xs font-bold text-slate-700 transition-colors" placeholder="-" />
               </div>
               <div className="flex flex-col">
                 <span className="text-xs uppercase tracking-wider text-blue-700 font-bold mb-0.5">Area</span>
                 <input id="doctor-area-input" type="text" value={doctorArea} onChange={e=>setDoctorArea(e.target.value)} className="w-full bg-slate-50/50 rounded flex-1 border border-slate-100 focus:border-blue-300 focus:bg-white outline-none px-1 py-0.5 text-xs font-bold text-slate-700 transition-colors" placeholder="-" />
               </div>
               <div className="flex flex-col">
                 <span className="text-xs uppercase tracking-wider text-blue-700 font-bold mb-0.5">Mobile</span>
                 <input id="doctor-mob-input" type="text" value={doctorMob} onChange={e=>setDoctorMob(e.target.value)} className="w-full bg-slate-50/50 rounded flex-1 border border-slate-100 focus:border-blue-300 focus:bg-white outline-none px-1 py-0.5 text-xs font-black text-slate-800 tracking-wider transition-colors" placeholder="-" />
               </div>
            </div>
          </div>
        </div>

        {/* Right Panel: Invoice Meta */}
        <div className="w-64 shrink-0 p-1 flex flex-col justify-between bg-white rounded-xl shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07)] border border-slate-100 relative overflow-hidden">
          {/* Subtle background decoration */}
          <div className="absolute top-0 right-0 w-24 h-24 bg-teal-50 rounded-full blur-2xl -mr-8 -mt-8 opacity-60"></div>
          
          <div className="relative z-10 text-center flex items-center justify-center gap-1 pb-1.5 mb-1 border-b border-slate-100">
             <div className="w-1 h-0.5 rounded-full bg-teal-500 animate-pulse"></div>
             <span className="text-xs font-black text-slate-700 tracking-[0.1em] uppercase">Sales Invoice</span>
          </div>
          
          <div className="relative z-10 flex flex-col justify-between h-full space-y-1">
            <div className="flex justify-between items-center bg-slate-50 px-2 py-1 rounded-md border border-slate-100">
              <span className="text-xs text-slate-500 font-bold">Date</span>
              <div className="flex items-center gap-1">
                <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">{getDayName(date).slice(0,3)}</span>
                <input type="date" value={date} onChange={e=>setDate(e.target.value)} className="w-[85px] outline-none bg-transparent font-black text-xs text-slate-800 cursor-pointer text-right" />
              </div>
            </div>
            
            <div className="flex justify-between items-center bg-slate-50 px-2 py-1 rounded-md border border-slate-100">
              <span className="text-xs text-slate-500 font-bold">Bill</span>
              <select value={billType} onChange={e=>setBillType(e.target.value)} className="w-[85px] text-xs text-right outline-none bg-transparent text-slate-800 font-black uppercase focus:text-teal-600 transition-colors cursor-pointer">
                <option value="Medicine">Medicine</option>
                <option value="General">General</option>
              </select>
            </div>
            
            <div className="flex justify-between items-center bg-slate-50 px-2 py-1 rounded-md border border-slate-100">
              <span className="text-xs text-slate-500 font-bold">Vch No</span>
              <input type="text" value={vchNo} onChange={e=>setVchNo(e.target.value)} className="w-[85px] text-xs text-right outline-none bg-transparent text-slate-800 font-black uppercase focus:text-teal-600 transition-colors" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Table Area (Sleek and Modern) */}
      <div className="bg-white rounded-xl shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07)] border border-slate-100 overflow-visible flex flex-col flex-1">
        <div className="overflow-auto w-full flex-1 rounded-t-xl">
          <table className="w-full text-left border-collapse whitespace-nowrap">
            <thead>
              <tr className="bg-gradient-to-r from-teal-800 to-teal-900 text-teal-50">
                <th className="py-1 px-1 text-xs font-black uppercase tracking-normal w-[24%] border-r border-teal-700/50 first:rounded-tl-xl">Product</th>
                <th className="py-1 px-1 text-xs font-black uppercase tracking-normal w-[6%] border-r border-teal-700/50 text-center">Packing</th>
                <th className="py-1 px-1 text-xs font-black uppercase tracking-normal w-[9%] border-r border-teal-700/50 text-center">Comp</th>
                <th className="py-1 px-1 text-xs font-black uppercase tracking-normal w-[9%] border-r border-teal-700/50 text-center">Batch no</th>
                <th className="py-1 px-1 text-xs font-black uppercase tracking-normal w-[6%] border-r border-teal-700/50 text-center">ExpDt</th>
                <th className="py-1 px-1 text-xs font-black uppercase tracking-normal w-[7%] border-r border-teal-700/50 text-right">MRP</th>
                <th className="py-1 px-1 text-xs font-black uppercase tracking-normal w-[8%] border-r border-teal-700/50 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <span>Rate</span>
                    <button 
                      type="button" 
                      onClick={toggleAllRates} 
                      title={showAllRates ? "Hide All Rates" : "Show All Rates"}
                      className="p-0.5 hover:bg-teal-700 rounded transition-colors text-teal-200 hover:text-white"
                    >
                      {showAllRates ? (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858-5.908a10.018 10.018 0 014.122-.963c4.478 0 8.268 2.943 9.542 7a9.978 9.978 0 01-2.13 3.592M3 3l18 18" /></svg>
                      )}
                    </button>
                  </div>
                </th>
                <th className="py-1 px-1 text-xs font-black uppercase tracking-normal w-[8%] border-r border-teal-700/50 text-right bg-teal-950/40">
                  <div className="flex items-center justify-end gap-1">
                    <span>Cost Rate</span>
                    <button 
                      type="button" 
                      onClick={() => setShowCostRate(!showCostRate)} 
                      title={showCostRate ? "Hide All Cost Rates" : "Show All Cost Rates"}
                      className="p-0.5 hover:bg-teal-700 rounded transition-colors text-teal-200 hover:text-white"
                    >
                      {showCostRate ? (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858-5.908a10.018 10.018 0 014.122-.963c4.478 0 8.268 2.943 9.542 7a9.978 9.978 0 01-2.13 3.592M3 3l18 18" /></svg>
                      )}
                    </button>
                  </div>
                </th>
                <th className="py-1 px-1 text-xs font-black uppercase tracking-normal w-[6%] border-r border-teal-700/50 text-right">Qty</th>
                <th className="py-1 px-1 text-xs font-black uppercase tracking-normal w-[12%] border-r border-teal-700/50 text-right">Amount</th>
                <th className="py-1 px-1 text-xs font-black uppercase tracking-normal w-[4%] text-center last:rounded-tr-xl">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50" onKeyDown={handleGridKeyDown}>
              {rows.map((row) => (
                <tr 
                  key={row.id} 
                  data-rowid={row.id} 
                  onClick={() => setSelectedRowId(row.id)}
                  className={`group transition-colors duration-200 ${row.id === selectedRowId ? 'bg-teal-50/70 ring-1 ring-teal-400' : 'hover:bg-slate-50/80'}`}
                >
                  <td className="py-0.5 px-1 border-r border-slate-100 relative">
                    <input 
                      type="text" 
                      value={row.productName}
                      readOnly
                      onClick={() => {
                        setSelectedRowId(row.id);
                        setActiveProductSearchRow(row.id);
                        setProductSearchQuery(row.productName || '');
                        setProductSuggestionIndex(0);
                      }}
                      onFocus={() => {
                        setSelectedRowId(row.id);
                        setActiveProductSearchRow(row.id);
                        setProductSearchQuery(row.productName || '');
                        setProductSuggestionIndex(0);
                      }}
                      className="w-full bg-transparent font-black text-xs text-slate-800 outline-none uppercase cursor-pointer placeholder:text-slate-300 placeholder:font-black placeholder:normal-case" 
                      placeholder="Click to search..."
                    />
                  </td>
                  
                  <td className="py-0.5 px-1 border-r border-slate-100"><input type="text" value={row.packing} onFocus={() => setSelectedRowId(row.id)} onChange={e=>updateRow(row.id, 'packing', e.target.value)} className="w-full bg-transparent font-bold text-xs text-slate-600 outline-none uppercase text-center focus:text-teal-600" /></td>
                  <td className="py-0.5 px-1 border-r border-slate-100"><input type="text" value={row.company} onFocus={() => setSelectedRowId(row.id)} onChange={e=>updateRow(row.id, 'company', e.target.value)} className="w-full bg-transparent font-bold text-xs text-slate-600 outline-none uppercase focus:text-teal-600" /></td>
                  <td className="py-0.5 px-1 border-r border-slate-100"><input type="text" value={row.batch} onFocus={() => setSelectedRowId(row.id)} onChange={e=>updateRow(row.id, 'batch', e.target.value)} className="w-full bg-transparent font-bold text-xs text-slate-600 outline-none uppercase focus:text-teal-600" /></td>
                  <td className="py-0.5 px-1 border-r border-slate-100"><input type="text" value={row.expDate} maxLength={5} placeholder="MM/YY" onFocus={() => setSelectedRowId(row.id)} onChange={(e) => {
                    let val = e.target.value.replace(/[^\d]/g, '');
                    if (val.length > 2) {
                      val = val.substring(0, 2) + '/' + val.substring(2, 4);
                    }
                    updateRow(row.id, 'expDate', val);
                  }} className="w-full bg-transparent font-bold text-xs text-slate-600 outline-none uppercase text-center focus:text-teal-600" /></td>
                  <td className="py-0.5 px-1 border-r border-slate-100"><input type="number" value={row.mrp} onFocus={() => setSelectedRowId(row.id)} onChange={e=>updateRow(row.id, 'mrp', e.target.value)} className="w-full bg-slate-50 hover:bg-slate-100 rounded focus:ring-1 focus:ring-teal-500/20 font-black text-xs text-slate-800 px-1 py-0.5 outline-none text-right transition-all border border-transparent focus:border-teal-300" /></td>
                  <td className="py-0.5 px-1 border-r border-slate-100">
                    <div className="relative flex items-center justify-end group/rate">
                      {showAllRates || visibleRateRowIds.has(row.id) ? (
                        <input 
                          type="number" 
                          value={row.rate} 
                          onFocus={() => setSelectedRowId(row.id)}
                          onChange={e=>updateRow(row.id, 'rate', e.target.value)} 
                          className="w-full bg-slate-50 hover:bg-slate-100 rounded focus:ring-1 focus:ring-teal-500/20 font-black text-xs text-slate-800 pr-5 pl-1 py-0.5 outline-none text-right transition-all border border-transparent focus:border-teal-300" 
                        />
                      ) : (
                        <input 
                          type="password" 
                          value={row.rate || ''} 
                          readOnly 
                          onFocus={() => setSelectedRowId(row.id)}
                          onClick={() => toggleRowRate(row.id)}
                          className="w-full bg-slate-50 hover:bg-slate-100 rounded font-black text-xs text-slate-400 pr-5 pl-1 py-0.5 outline-none text-right cursor-pointer select-none border border-transparent" 
                          placeholder="••••"
                        />
                      )}
                      <button 
                        type="button" 
                        tabIndex={-1}
                        onClick={() => toggleRowRate(row.id)} 
                        className={`absolute right-1 p-0.5 rounded transition-colors ${showAllRates || visibleRateRowIds.has(row.id) ? 'text-teal-600 hover:text-teal-800' : 'text-slate-400 hover:text-slate-600'}`}
                        title={showAllRates || visibleRateRowIds.has(row.id) ? "Hide Rate" : "View Rate"}
                      >
                        {showAllRates || visibleRateRowIds.has(row.id) ? (
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        ) : (
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858-5.908a10.018 10.018 0 014.122-.963c4.478 0 8.268 2.943 9.542 7a9.978 9.978 0 01-2.13 3.592M3 3l18 18" /></svg>
                        )}
                      </button>
                    </div>
                  </td>
                  {/* Cost Rate Cell */}
                  <td className={`py-0.5 px-1 border-r border-slate-100 ${row.id === selectedRowId ? 'bg-teal-50/70' : 'bg-slate-50/30'}`}>
                    <div className="text-right text-xs font-black text-teal-800 pr-1" title="Unit Cost Rate">
                      {showCostRate ? (
                        `₹${(parseFloat(row.costPrice !== undefined && row.costPrice !== '' ? row.costPrice : row.rate) || 0).toFixed(2)}`
                      ) : (
                        '••••'
                      )}
                    </div>
                  </td>
                  <td className="py-0.5 px-1 border-r border-slate-100"><input type="number" value={row.qty} onFocus={() => setSelectedRowId(row.id)} onChange={e=>updateRow(row.id, 'qty', e.target.value)} className="w-full bg-slate-50 hover:bg-slate-100 rounded focus:ring-1 focus:ring-teal-500/20 font-black text-xs text-slate-800 px-1 py-0.5 outline-none text-right transition-all border border-transparent focus:border-teal-300" /></td>
                  <td className="py-0.5 px-2 border-r border-slate-100 text-right">
                    <span className="font-black text-emerald-600 text-xs">{(parseFloat(row.amount) || 0).toFixed(2)}</span>
                  </td>
                  <td className="py-0.5 px-1 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button 
                        type="button" 
                        onClick={() => insertRowAfter(row.id)} 
                        className="text-teal-600 hover:text-white p-1 rounded-md hover:bg-teal-600 transition-all border border-teal-200/60 hover:border-teal-600 active:scale-95 shadow-2xs" 
                        title="Add Another Product Below"
                      >
                        <svg className="w-3 h-3 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4"/></svg>
                      </button>
                      <button 
                        type="button" 
                        onClick={() => removeRow(row.id)} 
                        disabled={rows.length === 1 && !row.productName} 
                        className="text-slate-300 hover:text-rose-600 transition-colors p-1 rounded-md hover:bg-rose-50 disabled:opacity-20 disabled:hover:bg-transparent"
                        title="Delete Row"
                      >
                        <svg className="w-3 h-3 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Premium Footer Area */}
      <div className="bg-white rounded-xl shadow-[0_2px_12px_-3px_rgba(0,0,0,0.06)] border border-slate-100 flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-slate-100 font-bold text-xs text-slate-600 overflow-hidden shrink-0 mb-1">
        
        {/* Left Panel - Info & Actions */}
        <div className="w-full md:w-[32%] p-1.5 flex flex-col justify-between bg-slate-50/40">
          <div className="grid grid-cols-2 gap-1.5 mb-1.5">
            <div className="flex justify-between items-center bg-white px-2 py-0.5 rounded-md border border-slate-200 shadow-sm">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Items</span> 
              <span className="text-xs font-black text-slate-800">{validRows.length}</span>
            </div>
            <div className="flex justify-between items-center bg-white px-2 py-0.5 rounded-md border border-slate-200 shadow-sm">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Total Qty</span> 
              <span className="text-xs font-black text-slate-800">{totalQty}</span>
            </div>
          </div>
          
          <div className="relative">
            <textarea 
              value={narration} 
              onChange={e=>setNarration(e.target.value)} 
              className="w-full bg-white border border-slate-200 focus:border-teal-400 focus:ring-1 focus:ring-teal-500/20 px-2 py-0.5 outline-none rounded-md transition-all resize-none shadow-sm h-[26px] text-[11px] font-bold placeholder:text-slate-400 placeholder:font-medium" 
              placeholder="Narration / Notes..." 
            />
          </div>
        </div>

        {/* Middle Panel - Extra Details & Actions */}
        <div className="flex-1 p-1.5 flex flex-col justify-between bg-white space-y-1">
           <div className="flex items-center justify-between bg-slate-50 rounded-md px-2 py-0.5 border border-slate-100 text-[10px]">
             <span className="uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1">
               <span>Cost Rate</span>
               {activeSelectedRow?.productName && (
                 <span className="text-teal-700 font-black truncate max-w-[90px]" title={`Cost Rate of ${activeSelectedRow.productName}`}>
                   ({activeSelectedRow.productName})
                 </span>
               )}
               <button 
                 type="button" 
                 onClick={() => setShowCostRate(!showCostRate)} 
                 className="text-slate-400 hover:text-teal-600 transition-colors p-0.5"
                 title={showCostRate ? "Hide Cost Rate" : "View Cost Rate"}
               >
                 {showCostRate ? (
                   <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                 ) : (
                   <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858-5.908a10.018 10.018 0 014.122-.963c4.478 0 8.268 2.943 9.542 7a9.978 9.978 0 01-2.13 3.592M3 3l18 18"/></svg>
                 )}
               </button>
             </span>
             <div className="flex items-center gap-1">
               <input 
                 type={showCostRate ? "number" : "password"} 
                 value={costRate} 
                 onChange={e=>setCostRate(e.target.value)} 
                 className="w-16 bg-white border border-slate-200 focus:border-teal-400 rounded text-right font-black outline-none py-0.5 px-1 shadow-sm text-[11px] text-teal-800" 
                 placeholder="0.00" 
                 title={activeSelectedRow?.productName ? `Cost Rate for ${activeSelectedRow.productName} (Total Bill Cost: ₹${totalCost.toFixed(2)})` : `Total Cost: ₹${totalCost.toFixed(2)}`}
               />
             </div>
           </div>
           
           <div className="flex justify-between items-center bg-slate-50 px-2 py-0.5 rounded-md border border-slate-200 text-[10px]">
              <span className="text-slate-600 font-black uppercase tracking-wider">Type</span>
              <select id="payment-type-select" value={saleType} onChange={e=>{ const val = e.target.value; setSaleType(val); if (val === 'CREDIT' && !custName.trim()) { setShowCreditAlertModal(true); } }} onClick={e=>{ if (e.target.value === 'CREDIT' && !custName.trim()) { setShowCreditAlertModal(true); } }} className="w-[85px] outline-none bg-white py-0.5 px-1 rounded border border-slate-300 text-[11px] text-right cursor-pointer text-teal-700 font-black shadow-sm">
                 <option>CREDIT</option>
                 <option>CASH</option>
                 <option>MIX</option>
                 <option>ONLINE 1</option>
                 <option>ONLINE 2</option>
              </select>
           </div>

           {saleType === 'MIX' && (
             <div className="space-y-0.5 bg-slate-50 p-1.5 rounded-md border border-slate-100 shadow-inner">
               <div className="flex justify-between items-center text-[10px]">
                 <span className="uppercase tracking-wider text-slate-500 font-bold">Cash</span>
                 <input type="number" value={mixCashAmount} onChange={e=>{
                   setMixCashAmount(e.target.value);
                   const val = parseFloat(e.target.value) || 0;
                   if (val <= netAmount) setMixOnlineAmount((netAmount - val).toFixed(2));
                 }} className="w-14 bg-white border border-slate-200 focus:border-teal-400 rounded text-right font-black outline-none py-0.5 px-1 shadow-sm text-[11px]" placeholder="0" />
               </div>
               <div className="flex justify-between items-center text-[10px]">
                 <span className="uppercase tracking-wider text-slate-500 font-bold">Online</span>
                 <input type="number" value={mixOnlineAmount} onChange={e=>{
                   setMixOnlineAmount(e.target.value);
                   const val = parseFloat(e.target.value) || 0;
                   if (val <= netAmount) setMixCashAmount((netAmount - val).toFixed(2));
                 }} className="w-14 bg-white border border-slate-200 focus:border-teal-400 rounded text-right font-black outline-none py-0.5 px-1 shadow-sm text-[11px]" placeholder="0" />
               </div>
             </div>
           )}

           <div className="flex gap-1.5 pt-0.5 mt-auto">
             <button type="button" onClick={handlePayOnline} className="flex-1 py-1.5 text-[11px] rounded-md text-white font-black tracking-wider uppercase shadow transition-all hover:-translate-y-0.5 bg-gradient-to-r from-blue-600 to-blue-500">
               Pay
             </button>
             <button id="save-bill-btn" type="button" onClick={() => handleSaveBill()} disabled={isSaving} className="flex-1 py-1.5 text-[11px] rounded-md text-white font-black tracking-wider uppercase shadow transition-all hover:-translate-y-0.5 bg-emerald-600 hover:bg-emerald-700">
               {isSaving ? 'Saving...' : (editingBillId ? 'UPDATE' : 'Save')}
             </button>
             <button type="button" onClick={cancelEditing} className="flex-1 py-1.5 text-[11px] rounded-md text-slate-600 bg-slate-100 hover:bg-slate-200 font-black tracking-wider uppercase transition-all hover:-translate-y-0.5">
               Cancel
             </button>
           </div>
        </div>

        {/* Right Panel - Calculation & Payment Summary */}
        <div className="w-full md:w-[36%] p-2.5 sm:p-3 flex flex-col justify-between bg-gradient-to-b from-white to-slate-50 border-t md:border-t-0 md:border-l border-slate-200 space-y-1.5">
          
          <div className="space-y-1.5">
            {/* 1. Gross Amount */}
            <div className="flex justify-between items-center bg-blue-50/80 border border-blue-200/70 rounded-lg px-2.5 py-1 shadow-sm">
              <span className="text-[11px] uppercase tracking-wider text-blue-700 font-bold">Gross Total</span>
              <span className="font-black text-slate-900 text-sm sm:text-base tracking-tight">₹ {grossAmount.toFixed(2)}</span>
            </div>
          </div>
          
          {/* 3. Net Payable Box */}
          <div>
            <div className="flex flex-col bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 text-white px-3 py-1.5 rounded-xl shadow-md relative overflow-hidden ring-1 ring-emerald-500/30">
              <span className="text-[10px] uppercase tracking-widest opacity-95 font-black text-emerald-100">Net Payable</span>
              <div className="flex justify-between items-baseline mt-0.5">
                <span className="text-base font-bold text-emerald-200">₹</span>
                <span className="text-2xl sm:text-3xl font-black tracking-tight drop-shadow-sm">{Math.round(netAmount).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* 4. Amount Received, Bill Paid & Balance Due */}
          {(() => {
            const finalNet = Math.round(netAmount);
            const recVal = parseFloat(amountReceived) || 0;
            let effectivePaid = recVal > 0 ? Math.min(recVal, finalNet) : (saleType === 'CASH' || saleType === 'ONLINE' ? finalNet : 0);
            if (saleType === 'MIX') {
              effectivePaid = Math.min((parseFloat(mixCashAmount) || 0) + (parseFloat(mixOnlineAmount) || 0), finalNet);
            }
            const pendingBal = Math.max(0, finalNet - effectivePaid);

            return (
              <div className="space-y-1.5 pt-0.5">
                <div className="flex justify-between items-center bg-emerald-50/70 p-1.5 rounded-lg border border-emerald-200 shadow-sm">
                  <span className="text-[10px] uppercase tracking-wider text-emerald-900 font-extrabold">Received Amount (₹)</span>
                  <input 
                    id="amount-received-input"
                    type="number"
                    value={amountReceived}
                    onChange={e => setAmountReceived(e.target.value)}
                    placeholder={saleType === 'CASH' ? finalNet.toString() : "0.00"}
                    className="w-24 bg-white border border-emerald-300 rounded py-0.5 px-1.5 text-xs text-right font-black text-emerald-950 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-400"
                  />
                </div>

                {/* Received vs Balance Breakdown Cards */}
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="flex flex-col bg-emerald-50 p-1.5 rounded-lg border border-emerald-200">
                    <span className="text-[9px] uppercase tracking-wider text-emerald-700 font-extrabold">Bill Paid</span>
                    <span className="font-black text-emerald-800 text-xs mt-0.5">₹ {effectivePaid.toFixed(2)}</span>
                  </div>
                  <div className={`flex flex-col p-1.5 rounded-lg border ${pendingBal > 0 ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'}`}>
                    <span className={`text-[9px] uppercase tracking-wider font-extrabold ${pendingBal > 0 ? 'text-rose-700' : 'text-slate-500'}`}>
                      {pendingBal > 0 ? 'Balance Due' : 'Balance'}
                    </span>
                    <span className={`font-black text-xs mt-0.5 ${pendingBal > 0 ? 'text-rose-700' : 'text-slate-700'}`}>
                      ₹ {pendingBal.toFixed(2)}
                    </span>
                  </div>
                </div>

                {recVal > finalNet && (
                  <div className="flex justify-between items-center bg-blue-50 px-2 py-1 rounded-lg border border-blue-200 text-xs">
                    <span className="text-[10px] uppercase tracking-wider text-blue-700 font-bold">Change Return</span>
                    <span className="font-black text-blue-700">₹ {(recVal - finalNet).toFixed(2)}</span>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Add Customer Modal */}
      {showCustomerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-100">
            <div className="px-5 py-1 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-teal-50 to-white">
              <h3 className="text-base font-black text-teal-900 flex items-center gap-2">
                <div className="w-2 h-6 rounded-full bg-teal-400"></div>
                Add New Customer
              </h3>
              <button onClick={() => setShowCustomerModal(false)} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-full transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            
            <div className="p-2 space-y-1">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Customer Name *</label>
                <input type="text" value={newCustomer.name} onChange={e => setNewCustomer({...newCustomer, name: e.target.value})} className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-xs font-black rounded-xl px-3 py-1 outline-none focus:border-teal-400 focus:bg-white transition-colors uppercase placeholder:normal-case placeholder:font-black placeholder:text-slate-300" placeholder="Enter customer name" autoFocus />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Address</label>
                <textarea value={newCustomer.address} onChange={e => setNewCustomer({...newCustomer, address: e.target.value})} className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-xs font-bold rounded-xl px-3 py-1 outline-none focus:border-teal-400 focus:bg-white transition-colors resize-none h-0.56" placeholder="Enter full address"></textarea>
              </div>
              
              <div className="flex gap-2">

                <div className="flex-1">
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Mobile No</label>
                  <input type="text" value={newCustomer.phone} onChange={e => setNewCustomer({...newCustomer, phone: e.target.value})} className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-xs font-bold rounded-xl px-3 py-1 outline-none focus:border-teal-400 focus:bg-white transition-colors tracking-widest" placeholder="9876543210" />
                </div>
              </div>
            </div>
            
            <div className="px-5 py-1 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-3">
              <button onClick={() => setShowCustomerModal(false)} className="px-4 py-1 text-xs font-bold text-slate-500 hover:bg-slate-200 rounded-xl transition-colors">Cancel</button>
              <button onClick={handleSaveCustomer} className="px-6 py-1 text-xs font-bold text-white bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-700 hover:to-teal-600 rounded-xl shadow-md transition-all hover:-translate-y-0.5">Save Customer</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Doctor Modal */}
      {showDoctorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-100">
            <div className="px-5 py-1 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-blue-50 to-white">
              <h3 className="text-base font-black text-blue-900 flex items-center gap-2">
                <div className="w-2 h-6 rounded-full bg-blue-400"></div>
                Add New Doctor
              </h3>
              <button onClick={() => setShowDoctorModal(false)} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-full transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            
            <div className="p-2 space-y-1">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Doctor Name *</label>
                <input type="text" value={newDoctor.name} onChange={e => setNewDoctor({...newDoctor, name: e.target.value})} className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-xs font-black rounded-xl px-3 py-1 outline-none focus:border-blue-400 focus:bg-white transition-colors uppercase placeholder:normal-case placeholder:font-black placeholder:text-slate-300" placeholder="Enter doctor name" autoFocus />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Address</label>
                <textarea value={newDoctor.address} onChange={e => setNewDoctor({...newDoctor, address: e.target.value})} className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-xs font-bold rounded-xl px-3 py-1 outline-none focus:border-blue-400 focus:bg-white transition-colors resize-none h-0.56" placeholder="Enter full address"></textarea>
              </div>
              
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Area</label>
                  <input type="text" value={newDoctor.area} onChange={e => setNewDoctor({...newDoctor, area: e.target.value})} className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-xs font-bold rounded-xl px-3 py-1 outline-none focus:border-blue-400 focus:bg-white transition-colors tracking-widest" placeholder="Doctor Area" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Mobile No</label>
                  <input type="text" value={newDoctor.phone} onChange={e => setNewDoctor({...newDoctor, phone: e.target.value})} className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-xs font-bold rounded-xl px-3 py-1 outline-none focus:border-blue-400 focus:bg-white transition-colors tracking-widest" placeholder="9876543210" />
                </div>
              </div>
            </div>
            
            <div className="px-5 py-1 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-3">
              <button onClick={() => setShowDoctorModal(false)} className="px-4 py-1 text-xs font-bold text-slate-500 hover:bg-slate-200 rounded-xl transition-colors">Cancel</button>
              <button onClick={handleSaveDoctor} className="px-6 py-1 text-xs font-bold text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 rounded-xl shadow-md transition-all hover:-translate-y-0.5">Save Doctor</button>
            </div>
          </div>
        </div>
      )}

      {showUpiModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl border border-slate-100 overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600 to-teal-500 p-4 flex justify-between items-center text-white">
              <h3 className="font-black tracking-widest uppercase text-sm">Pay Online via UPI</h3>
              <button onClick={() => setShowUpiModal(false)} className="hover:bg-white/20 p-1 rounded-full transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            <div className="p-6 flex flex-col items-center">
              <div className="bg-white p-3 rounded-2xl shadow-sm border border-slate-100 mb-4">
                <QRCodeCanvas 
                  value={"upi://pay?pa=" + upiId + "&pn=Medical Store&am=" + Math.round(netAmount).toFixed(2) + "&cu=INR"} 
                  size={200}
                  level={"H"}
                  includeMargin={true}
                />
              </div>
              <div className="text-center w-full space-y-2">
                <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">Amount to Pay</p>
                <p className="text-3xl font-black text-teal-600">₹ {Math.round(netAmount).toFixed(2)}</p>
                
                <div className="mt-4 pt-4 border-t border-slate-100 flex flex-col items-center w-full">
                   <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Your UPI ID (For receiving)</label>
                   <input type="text" value={upiId} onChange={e => handleSaveUpiId(e.target.value)} className="w-full text-center bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-xl px-3 py-2 outline-none focus:border-teal-400 transition-colors" placeholder="yourname@upi" />
                </div>
              </div>
            </div>
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-3">
               <button onClick={() => setShowUpiModal(false)} className="flex-1 py-2 rounded-xl text-xs font-bold text-slate-500 hover:bg-slate-200 transition-colors uppercase tracking-widest">Cancel</button>
               <button onClick={() => {
                 setSaleType('ONLINE');
                 handleSaveBill('ONLINE');
                 setShowUpiModal(false);
               }} className="flex-1 py-2 rounded-xl text-xs font-black text-white bg-teal-600 hover:bg-teal-700 shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5 uppercase tracking-widest">Mark as Paid</button>
            </div>
          </div>
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept=".pdf, .jpg, .jpeg, .png"
        multiple
      />

      {activeProductSearchRow && (() => {
        const addedProductNames = (rows || [])
          .filter(r => r.id !== activeProductSearchRow)
          .map(r => (r.productName || '').trim().toLowerCase())
          .filter(Boolean);

        const filteredProducts = combinedProducts.filter(m => {
          if (addedProductNames.includes(m.name?.trim().toLowerCase())) return false;
          if (getProductAvailableStock(m.name) <= 0) return false;
          if (!productSearchQuery.trim()) return true;
          return m.name.toLowerCase().includes(productSearchQuery.toLowerCase());
        });

        return (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200" onMouseDown={() => setActiveProductSearchRow(null)}>
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl flex flex-col h-[70vh] max-h-[600px] overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-100" onMouseDown={e => e.stopPropagation()}>
               {/* Header with Search Input */}
               <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center gap-3 shrink-0">
                 <div className="p-2 bg-teal-100 text-teal-600 rounded-full">
                   <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                 </div>
                 <input 
                   autoFocus
                   type="text"
                   value={productSearchQuery}
                   onChange={e => { setProductSearchQuery(e.target.value); setProductSuggestionIndex(0); }}
                   onKeyDown={(e) => {
                     if (e.key === 'ArrowDown') {
                       e.preventDefault();
                       setProductSuggestionIndex(Math.min(productSuggestionIndex + 1, filteredProducts.length - 1));
                     } else if (e.key === 'ArrowUp') {
                       e.preventDefault();
                       setProductSuggestionIndex(Math.max(0, productSuggestionIndex - 1));
                     } else if (e.key === 'Enter') {
                       e.preventDefault();
                       if (filteredProducts.length > 0) {
                         const m = filteredProducts[Math.max(0, productSuggestionIndex)];
                         selectProduct(m, activeProductSearchRow);
                       } else if (productSearchQuery.trim()) {
                         // Custom product
                         updateRow(activeProductSearchRow, 'productName', productSearchQuery);
                         setActiveProductSearchRow(null);
                       }
                     } else if (e.key === 'Escape') {
                       setActiveProductSearchRow(null);
                     }
                   }}
                   placeholder="Search products by name..."
                   className="w-full bg-transparent text-lg font-black text-slate-800 outline-none uppercase placeholder:text-slate-300 placeholder:normal-case placeholder:font-bold"
                 />
                 <button onClick={() => setActiveProductSearchRow(null)} className="p-2 text-slate-400 hover:text-rose-500 rounded-full transition-colors bg-white shadow-sm border border-slate-100 hover:bg-rose-50 ml-auto shrink-0">
                   <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                 </button>
               </div>
               
               {/* Results List */}
               <div className="flex-1 overflow-y-auto p-2 bg-slate-50/50">
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((m, idx) => {
                        let lastPacking = m.packing || m.strip || '';
                        if (!lastPacking && allSales) {
                          allSales.forEach(bill => {
                            (bill.items || []).forEach(item => {
                              if (item.productName?.toUpperCase() === m.name.toUpperCase() && item.packing) {
                                lastPacking = item.packing;
                              }
                            });
                          });
                        }
                        m.packing = lastPacking; // Update m to render correctly in suggestions and populate when clicked

                        const currentStock = getProductAvailableStock(m.name);
                        const isSelected = productSuggestionIndex === idx;

                        return (
                          <div 
                            key={idx}
                            className={'px-4 py-3 mb-1 rounded-xl cursor-pointer border transition-all flex justify-between items-center group/item ' + (isSelected ? 'bg-teal-50 border-teal-200 shadow-sm' : 'bg-white border-transparent hover:border-slate-200 hover:bg-slate-50')}
                            onMouseMove={() => {
                              if (productSuggestionIndex !== idx) setProductSuggestionIndex(idx);
                            }}
                            onClick={() => selectProduct(m, activeProductSearchRow)}
                          >
                            <div>
                              <div className={'font-black text-sm transition-colors flex items-center gap-2 ' + (isSelected ? 'text-teal-700' : 'text-slate-800 group-hover/item:text-teal-700')}>
                                <span>{m.name}</span>
                                <span className={'text-[10px] font-black uppercase px-2 py-0.5 rounded ' + ((m.category || '').toLowerCase().includes('human') ? 'bg-sky-100 text-sky-700 border border-sky-200' : 'bg-rose-100 text-rose-700 border border-rose-200 shadow-xs')}>
                                  {(m.category || '').toLowerCase().includes('human') ? 'HUMAN' : '🔴 VET'}
                                </span>
                              </div>
                              <div className="text-sm font-bold text-slate-400 uppercase tracking-widest mt-1 flex gap-3 items-center">
                                {m.company && <span>{m.company}</span>}
                                {m.packing && (
                                  <>
                                    <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                                    <span>Pkg: {m.packing}</span>
                                  </>
                                )}
                                {m.batch && (
                                  <>
                                    <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                                    <span>Batch: {m.batch}</span>
                                  </>
                                )}
                                <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                                <span>MRP: ₹{parseFloat(m.mrp || 0).toFixed(2)}</span>
                              </div>
                            </div>
                            
                            <div className="flex flex-col items-end gap-1">
                              <span className={'px-2.5 py-1 text-xs rounded-lg font-bold tracking-wider uppercase ' + (currentStock > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700')}>
                                {currentStock > 0 ? 'Stock: ' + currentStock : 'Stock is not available'}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-black text-teal-800 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded shadow-2xs" title="Unit Cost Rate">
                                  <span>Cost: {showCostRate || visibleSearchRateIndices.has(idx) ? `₹${parseFloat(m.costPrice || m.costRate || m.rate || 0).toFixed(2)}` : '••••'}</span>
                                </span>
                                <span className="text-sm font-black text-slate-500 flex items-center gap-1">
                                  <span>Rate: {showAllRates || visibleSearchRateIndices.has(idx) ? `₹${parseFloat(m.rate || 0).toFixed(2)}` : '••••'}</span>
                                  <button 
                                    type="button" 
                                    onClick={(e) => { e.stopPropagation(); toggleSearchRate(idx); }}
                                    className="text-slate-400 hover:text-teal-600 p-0.5 rounded transition-colors"
                                    title={showAllRates || visibleSearchRateIndices.has(idx) ? "Hide Rate" : "View Rate"}
                                  >
                                    {showAllRates || visibleSearchRateIndices.has(idx) ? (
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                                    ) : (
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858-5.908a10.018 10.018 0 014.122-.963c4.478 0 8.268 2.943 9.542 7a9.978 9.978 0 01-2.13 3.592M3 3l18 18"/></svg>
                                    )}
                                  </button>
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center p-8 opacity-60">
                      <svg className="w-12 h-12 text-slate-300 mb-3 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg>
                      <p className="text-xs font-black text-slate-500 uppercase tracking-widest">No matching products found</p>
                      <p className="text-sm text-slate-400 mt-1 font-bold">Press ENTER to add as a custom product</p>
                    </div>
                  )}
               </div>
            </div>
          </div>
        );
      })()}

      {/* Side Drawer for Past Bills */}
      {showPastBillsDrawer && (
        <div className="fixed inset-0 z-[400] flex justify-end bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200" onMouseDown={() => setShowPastBillsDrawer(false)}>
          <div className="bg-white w-full max-w-md h-full shadow-2xl flex flex-col border-l border-slate-200 animate-in slide-in-from-right duration-300" onMouseDown={e => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 text-teal-600 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                </div>
                <div>
                  <h3 className="font-black text-slate-800">Past Sales Bills</h3>
                  <p className="text-xs font-bold text-slate-500">Click a bill to edit</p>
                </div>
              </div>
              <button onClick={() => setShowPastBillsDrawer(false)} className="p-2 text-slate-400 hover:text-rose-500 rounded-full transition-colors hover:bg-rose-50">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            
            <div className="p-3 border-b border-slate-100">
              <input 
                type="text" 
                value={searchPastBillQuery}
                onChange={e => setSearchPastBillQuery(e.target.value)}
                placeholder="Search by customer name, bill no..."
                className="w-full bg-slate-100 border-none outline-none px-4 py-2 rounded-xl text-sm font-bold text-slate-700 focus:ring-2 focus:ring-teal-500/20 transition-all"
              />
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
              {allSales
                .filter(b => 
                  !searchPastBillQuery || 
                  (b.custName && b.custName.toLowerCase().includes(searchPastBillQuery.toLowerCase())) ||
                  (b.vchNo && b.vchNo.toString().includes(searchPastBillQuery)) ||
                  (b.date && b.date.includes(searchPastBillQuery))
                )
                .map((bill, idx) => (
                  <div 
                    key={bill.id || idx} 
                    onClick={() => loadBillForEditing(bill)}
                    className="bg-white border border-slate-200 p-3 rounded-xl cursor-pointer hover:border-teal-400 hover:shadow-md transition-all group relative overflow-hidden"
                  >
                    <div className="flex justify-between items-start mb-1">
                      <div className="font-black text-slate-800 group-hover:text-teal-700 transition-colors">{bill.custName || 'Cash Sale'}</div>
                      <div className="text-xs font-black text-slate-500 bg-slate-100 px-2 py-0.5 rounded">Vch: {bill.vchNo}</div>
                    </div>
                    <div className="flex justify-between items-center text-xs font-bold text-slate-400 mt-2">
                      <span>{bill.date}</span>
                      <span className="font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-lg border border-emerald-100">
                        ₹{parseFloat(bill.netAmount || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
              ))}
              
              {allSales.length === 0 && (
                <div className="flex flex-col items-center justify-center h-40 text-center opacity-60">
                    <svg className="w-12 h-12 text-slate-300 mb-3 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg>
                    <p className="text-xs font-black text-slate-500 uppercase tracking-widest">No past bills found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Product Details Popup (Like Image) */}
      {activeProductDetailsRow && (() => {
        const row = rows.find(r => r.id === activeProductDetailsRow);
        if (!row) return null;
        
        const stockLeft = getProductAvailableStock(row.productName);
        const partyName = getProductSupplierInfo(row.productName, row.batch);

        const handleConfirmOk = () => {
          setActiveProductDetailsRow(null);
          setTimeout(() => {
            const paySelect = document.getElementById('payment-type-select');
            if (paySelect) {
              paySelect.focus();
            }
          }, 100);
        };

        return (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200" onMouseDown={() => setActiveProductDetailsRow(null)}>
            <div 
              className="bg-slate-100 border-2 border-[#1e5c5c] shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200" 
              onMouseDown={e => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleConfirmOk();
                }
              }}
            >
               {/* Header */}
               <div className="px-4 py-2 bg-[#1e5c5c] text-white font-black text-sm flex justify-between items-center shadow-sm z-10">
                 <span>Product Details: {row.productName} {row.company ? '- ' + row.company : ''}</span>
                 <button onClick={() => setActiveProductDetailsRow(null)} className="hover:text-red-300 text-lg leading-none transition-colors">&times;</button>
               </div>
               
               <div className="p-4 space-y-4 text-xs font-bold text-slate-700 bg-white relative">
                  {/* Top info section featuring Stock Left & Party/Supplier Name */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg shadow-sm">
                    <div className="flex flex-col">
                      <span className="text-slate-400 font-bold uppercase tracking-wider text-[10px] mb-0.5">Product</span>
                      <span className="font-black text-slate-800 text-xs truncate" title={row.productName}>{row.productName || '-'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-slate-400 font-bold uppercase tracking-wider text-[10px] mb-0.5">Company</span>
                      <span className="font-black text-slate-800 text-xs truncate" title={row.company}>{row.company || 'UNKNOWN'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-slate-400 font-bold uppercase tracking-wider text-[10px] mb-0.5">Stock Qty (Left)</span>
                      <span className="font-black text-emerald-700 text-xs bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded w-fit">
                        {stockLeft} units
                      </span>
                    </div>
                    <div className="flex flex-col col-span-2 md:col-span-1">
                      <span className="text-slate-400 font-bold uppercase tracking-wider text-[10px] mb-0.5">Party / Supplier</span>
                      <span className="font-black text-teal-800 text-xs truncate bg-teal-50 border border-teal-100 px-1.5 py-0.5 rounded" title={partyName}>
                        {partyName}
                      </span>
                    </div>
                  </div>

                  {/* Main form section featuring Qty, Batch no, Expiry dt, MRP, Rate, and Cost Rate */}
                  <div className="flex gap-6 p-2">
                     <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-3">
                       
                       <div className="flex gap-2 items-center col-span-2">
                          <label className="w-20 text-slate-500 text-right">Qty</label>
                          <input type="number" autoFocus value={row.qty} onChange={(e) => updateRow(row.id, 'qty', e.target.value)} className="w-24 border border-teal-400 bg-yellow-100/50 px-2 py-1 rounded outline-none text-right font-black focus:border-[#1e5c5c] focus:bg-yellow-100" />
                       </div>
                       
                       <div className="flex gap-2 items-center">
                          <label className="w-20 text-slate-500 text-right">Batch no</label>
                          <input type="text" value={row.batch} onChange={(e) => updateRow(row.id, 'batch', e.target.value)} className="w-32 border border-slate-300 px-2 py-1 rounded outline-none uppercase focus:border-[#1e5c5c]" />
                       </div>
                       
                       <div className="flex gap-2 items-center">
                          <label className="w-20 text-slate-500 text-right">Expiry dt.</label>
                          <input type="text" value={row.expDate} onChange={(e) => {
                            let val = e.target.value.replace(/[^\d]/g, '');
                            if (val.length > 2) val = val.substring(0, 2) + '/' + val.substring(2, 4);
                            updateRow(row.id, 'expDate', val);
                          }} placeholder="MM/YY" maxLength={5} className="w-24 border border-slate-300 px-2 py-1 rounded outline-none text-center uppercase focus:border-[#1e5c5c]" />
                       </div>

                       <div className="flex gap-2 items-center">
                          <label className="w-20 text-slate-500 text-right">MRP</label>
                          <input type="number" value={row.mrp} onChange={(e) => updateRow(row.id, 'mrp', e.target.value)} className="w-32 border border-slate-300 px-2 py-1 rounded outline-none text-right font-black focus:border-[#1e5c5c]" />
                       </div>

                       <div className="flex gap-2 items-center">
                          <label className="w-20 text-slate-500 text-right">Sale Rate</label>
                          <input type="number" value={row.rate} onChange={(e) => updateRow(row.id, 'rate', e.target.value)} className="w-32 border border-slate-300 px-2 py-1 rounded outline-none text-right font-black focus:border-[#1e5c5c]" />
                       </div>

                       <div className="flex gap-2 items-center col-span-2">
                          <label className="w-20 text-teal-800 font-black text-right">Cost Rate</label>
                          <input type="text" readOnly value={`₹ ${(parseFloat(row.costPrice !== undefined && row.costPrice !== '' ? row.costPrice : row.rate) || 0).toFixed(2)}`} className="w-32 border-2 border-teal-500 bg-teal-50 text-teal-800 px-2 py-1 rounded outline-none text-right font-black shadow-xs" />
                          <span className="text-[10px] text-teal-600 font-bold">(प्रति युनिट खरेदी / Cost Rate)</span>
                       </div>
                     </div>
                  </div>
               </div>
               
               {/* Buttons */}
               <div className="flex justify-end gap-3 px-4 py-3 bg-slate-100 border-t border-slate-200">
                  <button onClick={() => setActiveProductDetailsRow(null)} className="px-6 py-1.5 bg-slate-200 text-slate-600 rounded-lg shadow-sm hover:bg-slate-300 transition-colors font-black uppercase tracking-widest text-xs">Cancel</button>
                  <button onClick={handleConfirmOk} className="px-8 py-1.5 bg-[#1e5c5c] text-white rounded-lg shadow-md hover:bg-[#154242] transition-colors font-black uppercase tracking-widest text-xs">Ok</button>
               </div>
            </div>
          </div>
        );
      })()}

      {/* Credit Alert Modal Popup */}
      {showCreditAlertModal && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200" onMouseDown={() => setShowCreditAlertModal(false)}>
          <div className="bg-white border-2 border-amber-500 rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col items-center text-center animate-in zoom-in-95 duration-200" onMouseDown={e => e.stopPropagation()}>
            <div className="w-14 h-14 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mb-4 text-2xl font-bold">
              ⚠️
            </div>
            <h3 className="text-lg font-black text-slate-800 mb-2">Customer Details Required</h3>
            <div className="bg-amber-50 border border-amber-200 text-amber-900 font-black text-sm px-4 py-3 rounded-xl mb-6 w-full">
              fill the before coustommer details
            </div>
            <button
              onClick={() => {
                setShowCreditAlertModal(false);
                setTimeout(() => {
                  const input = document.getElementById('cust-name-input');
                  if (input) {
                    input.focus();
                    if (input.select) input.select();
                  }
                }, 100);
              }}
              className="w-full py-2.5 bg-[#1e5c5c] text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-[#154242] shadow-lg transition-all"
            >
              OK (Fill Customer Details)
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default Sales;
